/**
 * Cowork API client for the omni-app.
 * Adapted from omniad-app/frontend/src/api/coworkApi.ts
 * - Auth headers injected automatically from omni-app's auth storage
 * - teamId/userId removed from all method signatures (backend extracts from JWT)
 */

import type {
  Agent,
  Artifact,
  Skill,
  SkillSession,
  BrandGraph,
  BrandListItem,
  Task,
  TaskDeliverable,
  TaskAttachment,
  TaskComment,
  OKR,
  Dashboard,
  DashboardDelivery,
  Project,
  ProjectTemplate,
  BrandPulse,
  LibraryFile,
  AudienceSegment,
  Competitor,
} from '../types/cowork';
import { getTokenFromStorage } from '@/utils/auth';
import { staffScopeHeaders } from '@/cowork/staffScope';

/** Per-deck brand overrides for report decks (presentations.settings). */
export interface TeamMember { id: string; name: string; title: string | null; email: string | null; phone: string | null; pictureUrl: string | null; source: 'employees' | 'account-manager' | 'campaign-managers' }

/** Mirrors CreatorFacts in cowork-cloud backend/src/routes/presentations.ts. */
export interface CreatorFacts {
  id: string; handle: string; platform: string; name: string; permalink: string; avatarUrl: string | null;
  followers: number | null; engagementRate: number | null; gender: 'F' | 'M' | null; ageGroup: string | null;
  country: string | null; location: string | null; language: string | null; bio: string | null; interests: string[]; contentCount: number | null;
  audience: { topCountries: Array<{ code: string; pct: number }>; topCities: Array<{ city: string; pct: number }>; femalePct: number | null; topAgeRange: string | null; realFollowersPct: number | null } | null;
  fetchedAt: string;
}

export interface DeckSettings {
  logoUrl?: string | null;
  brandName?: string | null;
  primaryColor?: string | null;
  /** Second brand colour (#hex): gradients, section accents, the second chart series. */
  accentColor?: string | null;
  /** Deck-wide font family (a curated Google font); overrides the Brand OS font. */
  fontFamily?: string | null;
  /** Deck palette (#hex, up to 8): chart series, shape fills, editor swatches. */
  palette?: string[] | null;
  /** Deck-wide page background / text colour overrides (#hex); per-page bgColor / textColor win. */
  backgroundColor?: string | null;
  textColor?: string | null;
  showLogo?: boolean;
  /** Beatly wordmark watermark top-right (default true). */
  beatlyMark?: boolean;
  /** Text-to-slides job state (set by POST /generate). */
  /** Proposal builder session — an in-progress Complete proposal resumes from here. */
  proposal?: Record<string, unknown>;
  /** A brief given at creation ("Build with Lykke"); the copilot plans from it the moment it connects, then clears it. */
  pendingBrief?: string;
  /** 'page' = the pending brief is one page to add; otherwise a whole deck. */
  pendingMode?: 'deck' | 'page';
  /** The brief was confirmed before the deck existed (strategy call, material): build every planned page at once. */
  pendingAutoBuild?: boolean;
  generation?: { status: 'running' | 'done' | 'failed'; startedAt: string; finishedAt?: string; error?: string; brief?: string; kind?: 'proposal' | 'kai' | 'freestyle'; /** Freestyle copy: timing, contract fixes, figures found in no source, the art direction. */ freestyle?: { ms?: number; failed?: number; fixes?: number; unsourced?: string[]; art?: Record<string, unknown>; sourceId?: string }; step?: number; stepsTotal?: number; stepLabel?: string; campaign?: { id?: string; name: string }; pages?: number; fetchComments?: boolean; flexiblePages?: boolean; comments?: { stored: number; posts: number; status: string; errors?: string[] }; aiContent?: AiContentOptions & { status?: string; items?: AiContentItem[]; errors?: string[] }; outline?: Array<Record<string, unknown>> };
}

/**
 * A planned page as the planner returns it. In the deck it becomes a real page at low fidelity: the
 * layout's blank slide with the title set and `_plan: GhostPlan` on it — edited in place, written in place.
 */
export interface PlanPage {
  id: string;
  /** Add-page catalogue id (the miniature). */
  layout: string;
  title: string;
  /** The writing brief for this page, the way an analyst briefs a designer. Editable. */
  description: string;
  elements: { text: boolean; image: boolean; chart: boolean; table: boolean };
  chartStyle?: string;
  needsData: boolean;
  status: 'planned' | 'building' | 'built' | 'skipped';
  /** The person's own notes for this page — appended to the brief when it is written. */
  notes?: string;
  /** Where the page landed in the deck once built (0-based). */
  builtAt?: number;
}
export interface DeckPlan { brief: string; pages: PlanPage[]; note?: string; createdAt: string }
/** The triage of a need: what the system proposes to make — accepted or redirected before anything exists. */
export type NeedProposal =
  | { kind: 'deck'; title: string; brief: string; pages: number; storyline: string; campaign?: { id: string; name: string }; why: string }
  | { kind: 'page'; title: string; brief: string; deck?: { id: string; title: string }; storyline: string; campaign?: { id: string; name: string }; why: string }
  | { kind: 'template'; title: string; brief: string; template: { id: string; name: string; description: string; needs: string }; campaign?: { id: string; name: string }; storyline: string; why: string }
  | { kind: 'answer'; title: string; answer: string; brief: string; why: string };

/** What a ghost page carries on the slide until it is written. */
export interface GhostPlan { description: string; notes?: string; needsData: boolean; chartStyle?: string; elements?: PlanPage['elements']; layout: string; building?: boolean }

/** AI concept visuals for a report — UGC-style by default; the user's own ideas override the style. */
export interface AiContentOptions { enabled?: boolean; prompt?: string; count?: number; aspect?: '9:16' | '4:5' | '1:1' | '16:9'; style?: 'ugc' | 'product' | 'lifestyle' | 'editorial' }
export interface AiContentItem { id: string; url: string; kind: 'image'; aspect: '9:16' | '4:5' | '1:1' | '16:9'; title: string; prompt: string; createdAt: string }

export const API_BASE_URL = (import.meta.env.VITE_COWORK_API_URL as string) || 'http://localhost:3001';

export type DeliverableKind =
  | 'presentation' | 'document' | 'image' | 'dashboard' | 'post' | 'report' | 'file';

export interface Deliverable {
  id: string;
  kind: DeliverableKind;
  title: string;
  source: string;
  sourceId: string;
  agentId?: string;
  agentName?: string;
  taskId?: string;
  brandId?: string;
  url?: string;
  thumbUrl?: string;
  mime?: string;
  sizeBytes?: number;
  createdAt: string;
}

// ── CM tooling types ────────────────────────────────────────────────────────

export interface CmConversation {
  id: number;
  inbox: string;
  title: string;
  lastMessage?: string;
  lastActivityAt?: string;
  unread: number;
  status: string;
  /** Campaign this thread anchors to, when known. */
  campaignId?: string;
  taskId?: string;
}

export interface CmInboxMessage {
  id: number;
  content: string;
  outgoing: boolean;
  senderName?: string;
  private: boolean;
  createdAt?: string;
}

export interface CmReconciliation {
  campaignId: string;
  campaignName?: string;
  status?: string;
  readyToClose: boolean;
  checklist: Array<{ id: string; label: string; pass: boolean; detail: string }>;
  diffs: Array<{ kind: string; planned: string; actual: string; note: string }>;
  financeCsvLine: string;
  note?: string;
}

export interface CmDraftReview {
  id: string;
  campaignId: string;
  productId: string;
  versionId: string;
  creatorHandle?: string;
  status: 'pending' | 'clean' | 'flagged' | 'error';
  verdict?: {
    reviewed: string;
    checks: Array<{ name: string; pass: boolean; note?: string }>;
    flags: string[];
    suggestedFeedback?: string;
  };
  createdAt: string;
}

export interface CmOutboundMessage {
  id: string;
  campaignId: string;
  campaignName?: string;
  influencerHandle?: string;
  purpose: string;
  subject: string;
  body: string;
  status: 'draft' | 'approved' | 'dismissed';
  createdBy?: string;
  createdAt: string;
}

/** Kickoff wizard — the startup meeting document, live (see engine/kickoff.ts). */
export interface KickoffField {
  id: string;
  label: string;
  kind: 'short' | 'long' | 'chips' | 'select' | 'date' | 'number' | 'countries' | 'languages' | 'url' | 'email' | 'manager' | 'lookup' | 'json';
  options?: string[];
  /** Display names for options whose stored value is a code (languages are ISO 639-1, pods are SWEDEN/WEST/NORWAY/EAST). */
  optionLabels?: Record<string, string>;
  hint?: string;
  /** Must be filled before the campaign can be submitted. */
  required?: boolean;
  /** Required only while another field holds one of these values (chips: any of them) — e.g. estimated impressions on an awareness campaign. */
  requiredWhen?: { field: string; in: string[] };
  /** For kind 'lookup': which system the picker searches. */
  source?: 'oneflow' | 'hubspot';
}

export interface KickoffLookupItem { id: string; name: string; subtitle: string; url: string; amount?: number; currency?: string; state?: string; updated?: string }
export interface KickoffFinance { suggestedTakeRatePct: number | null; samples: Array<{ campaign: string; budget: number; creatorCost: number; takeRatePct: number; currency: string }>; connected: { oneflow: boolean; hubspot: boolean } }

export interface KickoffImportResult {
  applied: Array<{ id: string; label: string; value: string | string[]; quote: string; source: string }>;
  skipped: Array<{ id: string; reason: string; value?: unknown }>;
  unresolved: string[];
  summary: string;
  notes: string[];
  missing: string[];
  kickoff: Kickoff | null;
}

export interface KickoffChatResult {
  say: string;
  applied: Array<{ id: string; label: string; value: string | string[] }>;
  skipped: Array<{ id: string; reason: string }>;
  missing: string[];
  kickoff: Kickoff | null;
}

export interface KickoffSection {
  id: string;
  title: string;
  fields: KickoffField[];
}

export interface Kickoff {
  id: string;
  teamId: string;
  sharing?: SharingInfo;
  brandId?: string;
  clientName: string;
  campaignName?: string;
  /** draft → (optional) built (pre-meeting draft campaign exists) → published. */
  status: 'draft' | 'built' | 'published';
  data: Record<string, string | string[]>;
  prefillNotes: Record<string, string>;
  createdBy?: string;
  buildTaskId?: string;
  publishedTaskId?: string;
  /** The campaign the scaffold created/reconciled on Beatly. */
  platformCampaignId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CmNeedsMe {
  manager: string | null;
  campaigns: Array<{ campaign_id: string; name: string; brand_name?: string; status: string; date_end?: string }>;
  contents: {
    cmReview: Array<{ campaign_id: string; campaign_name?: string; brand_name?: string; product_id: string; creator_handle?: string; beat_name?: string }>;
    cmFeedback: Array<{ campaign_id: string; campaign_name?: string; product_id: string; creator_handle?: string }>;
  };
  metrics: Array<{ campaign_id: string; campaign_name?: string; creator_handle?: string }>;
  payouts: Array<{ campaign_id: string; campaign_name?: string; creator_handle?: string }>;
  reviewsByCampaign?: Record<string, CmDraftReview[]>;
  outbox: { drafts: number; chaseDrafts: number };
  note?: string;
}

// ── Wizard 2.0 types ────────────────────────────────────────────────────────

export interface WizardObjective {
  code: 'AWARENESS' | 'TRAFFIC' | 'ENGAGEMENT' | 'LEADS' | 'APP_PROMOTION' | 'SALES';
  name: string;
  text: string;
  tags: string[];
  icon: string;
}

export interface WizardChannel {
  id: 'influencer' | 'ugc' | 'paid_social' | 'owned';
  name: string;
  description: string;
}

export interface TikTokTrends { countryCode: string; dateRange: string; category: string; industry: Array<{ id: string; name: string; rank: number; rankChange: string; views: number; posts: number; topCountries: string[] }>; all: Array<{ id: string; name: string; rank: number; rankChange: string; views: number; posts: number; topCountries: string[] }> }
export interface FamilyTags { format: 'creator-led' | 'studio' | 'product-only' | 'unknown'; hook: string; message: string; proof: string; offer: string; cta: string; product: string; archetype: string }
/** Discover → Ads Library: one ad as SearchApi returns it (creative URLs are Meta's own, valid for hours). */
export interface LibraryAdRow { id: string; pageId: string; pageName: string; pageLikes: number | null; start: string | null; end: string | null; active: boolean; platforms: string[]; title: string; body: string; cta: string; link: string; displayFormat: string; cards: number; isTemplate: boolean; image: string | null; video: string | null; videoPreview: string | null; snapshotUrl: string; /** TikTok: estimated people who saw it. */ audience?: string; audienceMin?: number | null; audienceMax?: number | null }
export interface LibraryPage { pageId: string; name: string; likes: number | null; igUsername: string | null; country: string | null; category: string | null }
export interface AdsLibraryResponse { configured: boolean; note?: string; error?: string; market?: string; countryCode?: string; query?: string; pages: LibraryPage[]; picked?: LibraryPage | null; ads: LibraryAdRow[]; nextPage?: string | null; competitors: string[]; total?: number | null; byKeyword?: boolean; platform?: 'meta' | 'tiktok' }
/** Radar library: an ad we hold — creative in our storage, `kept` = a person chose it. */
export interface RadarAsset { id: string; teamId: string; brandId: string | null; kind: 'meta_ad' | 'tiktok_ad' | 'google_ad' | 'ig_post' | 'tiktok_post'; source: string; externalId: string; advertiser: string; pageId: string | null; market: string; platforms: string[]; format: 'video' | 'image' | 'carousel' | 'text'; firstSeen: string | null; lastSeen: string | null; active: boolean; title: string; body: string; cta: string; link: string; imageUrl: string | null; videoUrl: string | null; tags: Record<string, unknown>; kept: boolean; keptBy: string | null; keptAt: string | null; capturedAt: string; updatedAt: string }
export interface RadarLibraryResponse { market: string; items: RadarAsset[]; total: number; advertisers: Array<{ advertiser: string; count: number; kept: number; videos: number }> }
/** Weekly media pressure per advertiser from our daily reach snapshots — a band, labelled by its basis. */
export interface Pressure { advertiser: string; market: string; asOf: string; people7d: number; people7dPrev: number | null; activeAds: number; newAds7d: number; impressions7d: number; spend7d: { low: number; high: number; currency: string } | null; basis: 'week' | 'lifetime' | 'none'; days: number; isBrand: boolean }
export interface PressureResponse { market: string; countryCode: string; brand: string; rows: Pressure[]; method: string }
/** Search interest (Google Trends) for the brand and its competitors on one scale. */
export interface DemandRow { name: string; isBrand: boolean; values: number[]; average: number; last4: number; prev4: number; changePct: number | null; peak: { date: string; value: number } | null; share: number | null }
export interface DemandResponse { configured: boolean; note?: string; error?: string; brand?: string; market?: string; geo?: string; range?: string; at?: string; dates?: string[]; rows?: DemandRow[]; related?: { top: Array<{ query: string; value: number }>; rising: Array<{ query: string; value: string }> } }
/** Where each competitor buys media, across the three public ad libraries. */
export interface PresenceRow { name: string; isBrand: boolean; website: string | null; meta: { active: number; newLast30: number; euReachActive: number } | null; tiktok: { advertiser: string; total: number | null; latest: string | null; recent: number } | null; google: { domain: string; total30d: number | null; image: number; video: number; text: number; latest: string | null; advertisers: string[] } | null; platforms: string[] }
export interface PresenceResponse { configured: boolean; note?: string; error?: string; brand?: string; market?: string; at?: string; rows?: PresenceRow[] }
/** Google AI Mode answers to the category's buying questions, and who is named in them. */
export interface AiAnswer { question: string; mentioned: string[]; cited: string[]; sources: Array<{ title: string; domain: string }>; excerpt: string }
export interface AiVisibilityResponse { configured: boolean; note?: string; error?: string; brand?: string; market?: string; category?: string; at?: string; questions?: string[]; answers?: AiAnswer[]; rows?: Array<{ name: string; isBrand: boolean; mentioned: number; cited: number; share: number }> }
/** Handle Watch: a competitor's official account as the creator database sees it. */
export interface SocialPost { id: string; permalink: string; preview: string | null; video: string | null; title: string; published: string | null; likes: number; comments: number; views: number | null; platform: 'INSTAGRAM' | 'TIKTOK' }
export interface HandleAccount { platform: 'INSTAGRAM' | 'TIKTOK'; handle: string; creatorId: string; followers: number | null; engagement: number | null; contentCount: number | null; audience: { genderAges: Array<{ gender: 'M' | 'F'; age: string; pct: number }>; countries: Array<{ code: string; pct: number }>; cities: Array<{ city: string; pct: number }>; followerTypes: Array<{ name: string; pct: number }>; women: number | null; home: number | null; top: string | null } | null; posts: SocialPost[]; postsPerWeek: number | null; lastPost: string | null; creatorsOnFeed: Array<{ handle: string; posts: number }> }
export interface HandleRow { name: string; isBrand: boolean; instagram: HandleAccount | { handle: string | null; missing: 'not-in-database' | 'no-handle' | 'not-market' } | null; tiktok: HandleAccount | null; site?: { instagram: string | null; tiktok: string | null; youtube: string | null } }
export interface HandleWatchResponse { brand?: string; market?: string; at?: string; rows?: HandleRow[]; note?: string; error?: string }
/** Creator Watch: creators seen with each competitor, with the signals that put them there. */
export type CreatorSignal = 'hashtag' | 'bio' | 'feed' | 'ad';
export interface WatchedCreator { creatorId: string | null; handle: string; name: string; platform: string; followers: number | null; engagement: number | null; avatar: string | null; permalink: string | null; interests: string[]; signals: CreatorSignal[]; onList: boolean | null; competitors: string[] }
export interface CreatorWatchRow { name: string; isBrand: boolean; hashtag: string; creators: WatchedCreator[]; counts: Record<CreatorSignal, number>; creatorLedFamilies: number; failed?: CreatorSignal[] }
export interface CreatorWatchResponse { brand?: string; market?: string; at?: string; rows?: CreatorWatchRow[]; shared?: WatchedCreator[]; onLists?: WatchedCreator[]; note?: string; error?: string; /** The watch is being built (24 database searches, a few minutes); poll. */ building?: boolean; startedAt?: string; partial?: boolean }
/** Comment pulse: what people say back to each competitor under its top posts. */
/** Campaign canvas: existing objects as nodes; positions are the only stored thing. */
export type CanvasKind = 'brand' | 'audience' | 'promotable' | 'reference' | 'casting' | 'plan' | 'kickoff' | 'brief' | 'note' | 'positioning' | 'copy' | 'creative' | 'direction' | 'storyboard' | 'prompt' | 'competitor' | 'intel' | 'report' | 'campaign' | 'sticky' | 'frame' | 'asset' | 'chart' | 'slot' | 'concept';
export type PromptTool = 'ask' | 'brief' | 'image' | 'video' | 'variations' | 'direction' | 'storyboard' | 'scenarios' | 'brand-check' | 'chart' | 'concepts';
/** Which engine draws a still: Google (fast, default) or Higgsfield Soul (photoreal, queued). */
export type ImageEngine = 'gemini' | 'higgsfield';
export interface CanvasChart { type: 'bar' | 'line'; labels: string[]; datasets: Array<{ label: string; values: number[] }>; unit?: string }
export interface CanvasResult { campaignId: string; campaign: string; impressions: number; reach: number; engagements: number; eri: number; spend: number; currency: string | null; publications: number; creators: number; start: string | null; end: string | null }
export interface CanvasBoardMeta { id: string; name: string; planId: string | null; blank: boolean; createdAt: string }
export type PickKind = 'brand' | 'audience' | 'promotable' | 'competitor' | 'market-brief' | 'ad' | 'pressure' | 'demand' | 'presence' | 'ai-visibility' | 'handles' | 'creator-watch' | 'comment-pulse' | 'creator-list' | 'plan' | 'kickoff' | 'deck' | 'campaign';
export interface CanvasCatalogue { market: string; groups: Array<{ label: string; items: Array<{ kind: PickKind; ref: string; title: string; subtitle?: string; market?: string }> }> }
export interface CanvasNode { id: string; kind: CanvasKind; stage: 'brand' | 'reach' | 'say' | 'cast' | 'plan'; title: string; pick?: { kind: PickKind; ref: string }; subtitle?: string; lines: string[]; badge?: { text: string; tone: 'ok' | 'warn' | 'muted' | 'accent' }; imageUrl?: string | null; colors?: string[]; split?: Array<{ label: string; pct: number }>; href: string; x: number; y: number; studio?: { status: 'draft' | 'kept' | 'approved' | 'generating' | 'failed'; from: 'vera' | 'call' | 'user'; text?: string; videoUrl?: string | null; format?: string; error?: string; parentIds: string[]; at: string; tool?: PromptTool; color?: string; width?: number; height?: number; historyLength?: number; approvedAt?: string | null; comments?: Array<{ id: string; by: string; text: string; at: string; resolved?: boolean }>; chart?: CanvasChart; result?: CanvasResult; count?: number; perInput?: boolean; pickKind?: PickKind; hint?: string }; ran?: { campaign: string; impressions: number; eri: number; spend: number; currency: string | null } }
export interface CanvasBoard { brandId: string; boardId: string; board: CanvasBoardMeta | null; boards: CanvasBoardMeta[]; planId: string | null; market: string | null; plans: Array<{ id: string; name: string }>; nodes: CanvasNode[]; edges: Array<{ from: string; to: string; kind: 'generated' | 'references' | 'user' }>; at: string }
export interface StudioNodeInput { id?: string; kind: 'brief' | 'note' | 'positioning' | 'audience' | 'casting' | 'copy' | 'creative' | 'direction' | 'storyboard' | 'prompt' | 'sticky' | 'frame' | 'asset' | 'slot' | 'concept'; title: string; lines?: string[]; text?: string; status?: 'draft' | 'kept' | 'approved'; from?: 'vera' | 'call' | 'user'; parentIds?: string[]; tool?: PromptTool; color?: string; width?: number; height?: number; count?: number; perInput?: boolean }
export interface CanvasScenario { budget: number; label: string; multiplier: number; split: Array<{ label: string; pct: number }>; creators: number; reach: number; reachPct: number | null; frequency: number | null; costPerThousandReached: number | null; impressions: number; takeRatePct: number | null }
export interface CanvasScenarios { planId: string; currency: string; universe: number; universeBasis: string; sized: boolean; gate: string; current: number; scenarios: CanvasScenario[]; assumptions: string[] }
export interface CanvasVariant { label: string; text: string; onBrand: boolean; note: string }
export interface PulseRow { name: string; isBrand: boolean; posts: number; comments: number; scored: number; positivePct: number | null; negativePct: number | null; avgScore: number | null; questionsPct: number; complaintsPct: number; purchasePct: number; praisePct: number; top: Array<{ authorName?: string | null; authorHandle?: string | null; authorAvatarUrl?: string | null; text: string; likes: number; platform: string; permalink: string; publishedAt?: string | null; sentiment?: string | null; intents?: string[] }>; examples: Record<string, Array<{ author: string | null; text: string; likes: number }>>; lastFetchedAt: string | null; error?: string }
export interface CommentPulseResponse { brand?: string; market?: string; at?: string; rows?: PulseRow[]; note?: string; configured?: boolean; pending?: boolean; error?: string }
export interface CreativeFamily { key: string; repAdId?: string; snippet: string; title: string; variants: number; activeVariants: number; firstSeen: string | null; lastSeen: string | null; daysLive: number; markets: string[]; platforms: string[]; euReach: number; sampleAdIds: string[]; snapshotUrl: string; landing: string; tags?: FamilyTags; creativeUrl?: string | null; videoUrl?: string | null }
export interface LibrarySignals { total: number; active: number; newLast7: number; newLast30: number; medianDaysLiveActive: number; longestActiveDays: number; velocityPerWeek: number; persistentFamilies: number; families: number; markets: string[]; platforms: Record<string, number>; euReachActive: number; euReachMedian: number; demo: Array<{ age: string; male: number; female: number }>; targetAges: string | null; targetGender: string | null; languages: string[]; payers: string[] }
export interface AdLibraryReport { name: string; countryCode: string; fetchedAt: string; pageIds: string[]; pageNames: string[]; signals: LibrarySignals; families: CreativeFamily[]; changes: Array<{ kind: string; text: string; since: string }>; error?: string }
export interface ComparisonRow { name: string; isBrand: boolean; active: number; newPerMonth: number; velocityPerWeek: number; persistentFamilies: number; medianDaysLive: number; markets: number; euReachActive: number; creatorLedPct: number | null; proofPct: number | null; priceLedPct: number | null; demonstrationPct: number | null; tagged: number }
export interface CompetitorAdsResponse { market: string; countryCode: string; brand: string; items: Array<{ name: string; isBrand: boolean; library: AdLibraryReport | null; libraryStatus: 'ready' | 'stale' | 'missing' }>; read: { generatedAt: string; headline: string; pushing: string[]; gaps: string[]; forClient: string[] } | null; comparison: ComparisonRow[]; changes: Array<{ kind: string; text: string; since: string; name: string }>; refreshing: boolean; refreshedAt?: string | null; refreshError?: string | null; libraryConfigured: boolean; note?: string }
export interface TrendingHashtagRow { id: string; name: string; rank: number; rankChange: string; views: number; posts: number; topCountries: string[] }
export interface TikTokTrendsResponse { market: string; countryCode: string; brand: string; connected: boolean; configured: boolean; category: string; guessedCategory: string; categories: string[]; range: string; hashtags: { industry: TrendingHashtagRow[]; all: TrendingHashtagRow[] }; fetchedAt: string | null; error: string | null; competitors: Array<{ name: string; isBrand: boolean; hashtag: string; posts: number; totalPlays: number; avgPlays: number; sponsoredPct: number; fetchedAt: string; top: Array<{ text: string; plays: number; likes: number; author: string; url: string; cover: string | null; sponsored: boolean; date: string | null }> }>; posts: Array<{ competitor: string; isBrand: boolean; text: string; plays: number; likes: number; author: string; url: string; cover: string | null; sponsored: boolean; date: string | null; videoId: string | null }>; trendingVideos: Array<{ hashtag: string; id: string; shareUrl: string; embedUrl: string }>; read: { generatedAt: string; category: string; headline: string; picks: Array<{ hashtag: string; why: string; idea: string }>; avoid: Array<{ hashtag: string; why: string }> } | null }
export interface CompetitorIntel {
  name: string; countryCode: string; fetchedAt: string;
  meta: { activeAds: number; platforms: string[]; since: string | null; pageLikes: number | null; samples: Array<{ title: string; body: string; cta: string; link: string; image: string | null; video: string | null; since: string | null; platforms: string[] }>; error?: string } | null;
  tiktok: { hashtag: string; posts: number; totalPlays: number; avgPlays: number; sponsoredShare: number; local?: boolean; countryCode?: string; top: Array<{ text: string; plays: number; likes: number; author: string; url: string; cover: string | null; sponsored: boolean; date: string | null }>; error?: string } | null;
}
export type ProposalStepId = 'market' | 'audience' | 'concept' | 'mediaplan' | 'targets';
export interface ProposalIntent {
  objective: WizardObjective['code']; budget: number; currency: string; market: string;
  product?: string; startDate?: string; endDate?: string; notes?: string; campaignName?: string;
  /** Picked from the Brand OS promotables: description · price · category · URL, so every step knows the actual offer. */
  productContext?: string;
  /** Language of every slide (default English) — generation writes in it, build translates the scaffolding. */
  language?: string;
}
export interface ProposalPrepare {
  objectives: WizardObjective[];
  channels: Array<{ id: string; name: string; kind: 'creator' | 'content' | 'paid'; description: string }>;
  markets: string[]; currencies: string[];
  bands: Array<{ id: string; label: string; followers: string; priceCombo: number; priceSingle: number; avgReach: number }>;
  defaults: { budget: number; currency: string; market: string; brandName?: string; products: string[] };
  /** From the last campaign — placeholders and Lykke's suggestions, never prefilled. */
  suggested: { budget?: number; currency?: string; market?: string } | null;
  /** What the agent already knows about this client from Beatly data (null when no history). */
  known: { summary: string; campaignCount: number; campaigns: Array<{ name: string; start?: string; end?: string; market?: string; budget?: number; currency?: string; impressions: number; eri: number | null; creators: number }>; topCreators: Array<{ name: string; handle: string; impressions: number; eri: number | null; campaigns: number }> } | null;
  visuals: boolean;
}

export interface WizardPrepare {
  objectives: WizardObjective[];
  channels: WizardChannel[];
  markets: string[];
  defaults: { budget?: number; market?: string; brandId?: string; brandName?: string };
}

export interface WizardIntent {
  objective: WizardObjective['code'];
  budget: number;
  market: string;
  product?: string;
  /** From the Brand OS promotable the user picked: description · price · category · URL. */
  productContext?: string;
  startDate?: string;
  endDate?: string;
  notes?: string;
}

export interface WizardPlan {
  marketSnapshot: string;
  audiences: Array<{ title: string; description: string; why: string; fromWiki: boolean }>;
  concepts: Array<{ title: string; hook: string; description: string; formats: string[]; why: string }>;
  castingSpec: { count: number; sizeBand: string; niches: string[]; language: string; mustHave: string };
  mediaplan: Array<{ channelId: WizardChannel['id']; percent: number; rationale: string }>;
}

/** One open gate: work stopped, waiting on the user. */
export interface NeedsMeItem {
  kind: 'curation' | 'content' | 'launch' | 'agent_approval';
  campaignId?: string;
  campaignName?: string;
  brandName?: string;
  count: number;
  title: string;
  detail: string;
  approvalId?: string;
}

export interface CampaignWork {
  campaignId: string;
  tasks: Array<{ id: string; title: string; status: string; agentId?: string; agentName?: string; updatedAt: string }>;
  deliverables: Deliverable[];
  sessions: Array<{ id: string; agentName?: string; messageCount: number; lastMessageAt: string }>;
  summary: { openTasks: number; doneTasks: number; deliverables: number; conversations: number };
}

export interface DeliverablesPage {
  deliverables: Deliverable[];
  nextCursor: string | null;
  counts: Record<string, number>;
}

/** Stages of a website → Brand OS build, in order. */
export type AutofillStage = 'crawling' | 'identity' | 'assets' | 'promotables' | 'audiences' | 'competitors' | 'profiles' | 'done' | 'error';

export interface AutofillJob {
  id: string;
  brandId: string;
  url: string;
  status: 'running' | 'done' | 'error';
  stage: AutofillStage;
  stagesDone: AutofillStage[];
  error?: string;
  startedAt: string;
  completedAt?: string;
}
const BASE_URL = API_BASE_URL;

// ── 402 (insufficient credits) event bus ────────────────────────
type UpgradeListener = () => void;
const upgradeListeners = new Set<UpgradeListener>();

/** Subscribe to 402 events. Returns an unsubscribe function. */
export function onUpgradeRequired(cb: UpgradeListener): () => void {
  upgradeListeners.add(cb);
  return () => { upgradeListeners.delete(cb); };
}

function emit402() {
  upgradeListeners.forEach(cb => cb());
}

// ── Plan / Checkout types ───────────────────────────────────────
export interface PlanInfo {
  plan: string;
  creditsRemaining: number;
  creditsTotal: number;
  currentPeriodEnd?: string;
}

// ── Auth helpers ─────────────────────────────────────────────────

/** Build headers with auth token for JSON requests */
export function authHeaders(includeContentType = true): Record<string, string> {
  const token = getTokenFromStorage();
  const headers: Record<string, string> = { ...staffScopeHeaders() }; // staff client switcher (ignored for non-staff)
  if (includeContentType) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// ── Event types (for SSE streaming) ──────────────────────────────

export type SeoAgentEvent =
  | { type: 'step'; message: string }
  | { type: 'tool_call'; name: string; input: Record<string, string> }
  | { type: 'tool_result'; name: string; toolUseId: string; preview: string }
  | { type: 'text_chunk'; content: string }
  | { type: 'done'; fullText: string }
  | { type: 'error'; message: string };

export type GoogleAdsAgentEvent =
  | { type: 'step'; message: string }
  | { type: 'credentials_missing'; missing: string[] }
  | { type: 'tool_call'; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; name: string; toolUseId: string; preview: string }
  | { type: 'write_proposed'; action: string; details: string; toolName: string; toolInput: Record<string, unknown> }
  | { type: 'text_chunk'; content: string }
  | { type: 'done'; fullText: string }
  | { type: 'report_created'; artifactId: string; title: string }
  | { type: 'history_update'; history: unknown[] }
  | { type: 'error'; message: string };

// ── HTTP helpers with auth ───────────────────────────────────────

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: authHeaders(false),
  });
  if (!res.ok) {
    if (res.status === 402) { emit402(); }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 402) { emit402(); }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 402) { emit402(); }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function del<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'DELETE',
    headers: authHeaders(false),
  });
  if (!res.ok) {
    if (res.status === 402) { emit402(); }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/** A failed request that keeps the HTTP status and the JSON body, for callers that act on a specific answer (e.g. 409). */
export class CoworkApiError extends Error {
  constructor(message: string, public status: number, public body: Record<string, unknown>) { super(message); this.name = 'CoworkApiError'; }
  get code(): string | undefined { return typeof this.body.code === 'string' ? this.body.code : undefined; }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 402) { emit402(); }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const json = (err && typeof err === 'object' ? err : {}) as Record<string, unknown>;
    throw new CoworkApiError((json.error as string) || `Request failed: ${res.status}`, res.status, json);
  }
  return res.json() as Promise<T>;
}

// ── API ──────────────────────────────────────────────────────────

export type DeckOp = 'navigate' | 'add' | 'rewrite' | 'move' | 'delete' | 'restyle' | 'layout' | 'title' | 'question' | 'none';
export type DeckRoute = 'act' | 'act_then_speak' | 'speak' | 'clarify' | 'confirm' | 'none';
export interface DeckCommand { route: DeckRoute; wantsVoice: boolean; underspecified: boolean; complete: boolean; op: DeckOp; confidence: number; target: number | null; destination: number | 'end' | null; layout: string | null; theme: 'light' | 'soft' | 'dark' | 'brand' | null; needsWriting: boolean; needsData: boolean; dataSection: string; brief: string; ms: number; source: 'jev' | 'heuristic' }
export type GuardrailSeverity = 'error' | 'warning' | 'info';
export interface GuardrailFinding { page: number; check: 'banned_term' | 'placeholder' | 'promises' | 'unsourced_stat' | 'cost_disclosure' | 'fact_contradicted' | 'fact_missing' | 'claim_unsupported' | 'chart_mismatch' | 'ledger_mismatch'; severity: GuardrailSeverity; label: string; hint: string; probability: number; excerpt: string }
export interface GuardrailReport { available: boolean; checkedAt: string; pages: number; ms: number; findings: GuardrailFinding[]; counts: Record<GuardrailSeverity, number>; unchecked: number; /** Figures checked against the deck's facts: its campaign when it names one, otherwise the brand's campaign ledger. `available: false` means nothing was verified — say so, never "clean". */ facts?: { available: boolean; reason?: string; source?: 'campaign' | 'brand'; figures: number; charts?: number; unjudged?: number; verified: number; unverified: number; judged: number; ms: number } }

export interface WikiQuality { score: number; coverage: number; provenance: number; freshness: number; pages: { total: number; real: number; facts: number; thin: number; stub: number }; byCategory: Record<string, { total: number; real: number }>; nextActions: string[]; computedAt: string }
export interface WikiEnrichStatus { running: boolean; brandId?: string; campaigns?: { seen: number; written: number; unchanged: number; skipped: number; claims: number; dropped: number }; stubs?: { drafted: string[]; skipped: string[] } | null; lint?: { pairs: number; found: number } | null; ms?: number; error?: string }

export interface StaffScopeTeam { id: string; name: string; brands: string[] }
export interface StaffScopeTeams { house: { id: string; name: string }; current: string; actorTeamId: string; total: number; teams: StaffScopeTeam[] }
export interface CoworkMe { user: { user_id: string; name?: string; email?: string; is_admin?: boolean }; teamId: string; staff: boolean; scope: { teamId: string; actorTeamId: string; brandId?: string } | null; /** Client surfaces open to this session's team (all of them for staff). */ features?: string[] }
export interface ClientFeatures { teamId: string; features: string[]; available: readonly string[] }

export type KnowledgeKind = 'upload' | 'link' | 'text' | 'scout' | 'trends';
export type KnowledgeStatus = 'processing' | 'ready' | 'failed' | 'skipped';
/** One source in Beatly's strategy pool (cowork /v1/knowledge, staff only). `snippets` is a count. */
export interface KnowledgeItem {
  id: string; kind: KnowledgeKind; title: string; sourceName: string | null; sourceUrl: string | null; publishedAt: string | null;
  addedAt: string; addedBy: string | null; status: KnowledgeStatus; error: string | null; summary: string | null;
  platforms: string[]; topics: string[]; fileName: string | null; snippets: number; stale: boolean;
}
export interface KnowledgeSnippet { id: string; itemId: string; label: string; text: string; platforms: string[]; topics: string[]; publishedAt: string | null; stale: boolean; createdAt: string }
export interface KnowledgeScoutRun { startedAt: string; finishedAt: string | null; by: string; added: Array<{ id: string; title: string; source: string; status: string; snippets: number }>; considered: number; errors: string[]; trends: { markets: number; hashtags: number } | null }
export interface KnowledgeScoutState { enabled: boolean; running: boolean; sources: Array<{ id: string; name: string; kind: string }>; last: KnowledgeScoutRun | null; current: KnowledgeScoutRun | null }

export const coworkApi = {
  /** Review on your phone: a one-campaign pass the desktop shows as a QR code (see cowork/mobileReview). */
  reviewPasses: {
    create: (body: { campaignId: string; campaignName?: string }): Promise<{ code: string; expiresAt: string; pending: number }> => post('/v1/review-passes', body),
    /** Is phone review switched on (the server holds its sealing key)? The button only shows when it is. `sample`: the
     *  sample posts can be opened too (local and staging only), for trying it without content waiting. */
    status: (): Promise<{ available: boolean; sample?: boolean }> => get('/v1/review-passes/status'),
  },
  /** Staff client switcher: who am I, and which client teams may I act for. */
  /**
   * The picked client's Content Hub for a BO session (admin token): the client endpoints (/app/v2/contenthub/*) refuse
   * admin tokens, so these go through the agents API, which asks /bo/v2 with this session's own token for the client
   * chosen in the BO top bar and keeps only that client's (and brand's) rows.
   */
  staffContentHub: {
    search: (p: { pageNumber?: number; pageSize?: number; text?: string | null; campaignId?: string | null; collaborationId?: string | null; kind?: string; brandId?: string | null }): Promise<import('@/api/beatlyApi').ContentHubMediaPage> => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(p)) if (v !== null && v !== undefined && v !== '') q.set(k, String(v));
      return get(`/v1/staff-contenthub/search?${q.toString()}`);
    },
    paths: (brandId?: string | null): Promise<{ folders?: import('@/api/beatlyApi').ContentHubPathRow[] }> =>
      get(`/v1/staff-contenthub/paths${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
  },

  staffScope: {
    me: async (): Promise<CoworkMe> => {
      const res = await fetch(`${BASE_URL}/v1/me`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error(`me ${res.status}`);
      return res.json();
    },
    teams: async (q = ''): Promise<StaffScopeTeams> => {
      const res = await fetch(`${BASE_URL}/v1/staff-scope/teams${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `teams ${res.status}`);
      return res.json();
    },
    /** One team by id (name + brand ids) — for the switcher label when the team is not in the current search page. */
    team: (teamId: string): Promise<{ team: { id: string; name: string; brands: Array<{ brandId: string; name: string }> } }> => get(`/v1/staff-scope/teams/${encodeURIComponent(teamId)}`),
    /** Which cowork surfaces a client team may use in the client app (staff only). */
    features: (teamId: string): Promise<ClientFeatures> => get(`/v1/staff-scope/teams/${encodeURIComponent(teamId)}/features`),
    setFeatures: (teamId: string, features: string[]): Promise<ClientFeatures> => put(`/v1/staff-scope/teams/${encodeURIComponent(teamId)}/features`, { features }),
  },
  /** Beatly's strategy pool: platform updates, research and trends every agent can use (staff only). */
  knowledge: {
    list: (f: { q?: string; kind?: string; platform?: string } = {}): Promise<{ items: KnowledgeItem[] }> => {
      const qs = new URLSearchParams(); if (f.q) qs.set('q', f.q); if (f.kind) qs.set('kind', f.kind); if (f.platform) qs.set('platform', f.platform);
      const tail = qs.toString(); return get(`/v1/knowledge/items${tail ? `?${tail}` : ''}`);
    },
    get: (id: string): Promise<{ item: KnowledgeItem; snippets: KnowledgeSnippet[] }> => get(`/v1/knowledge/items/${encodeURIComponent(id)}`),
    upload: async (files: File[], meta: { title?: string; sourceName?: string; publishedAt?: string } = {}): Promise<{ items: KnowledgeItem[] }> => {
      const fd = new FormData(); for (const f of files) fd.append('files', f, f.name);
      if (meta.title) fd.append('title', meta.title); if (meta.sourceName) fd.append('sourceName', meta.sourceName); if (meta.publishedAt) fd.append('publishedAt', meta.publishedAt);
      const res = await fetch(`${API_BASE_URL}/v1/knowledge/upload`, { method: 'POST', headers: authHeaders(false), body: fd });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Upload failed (${res.status})`); }
      return res.json();
    },
    addLink: (body: { url: string; title?: string; sourceName?: string }): Promise<{ item: KnowledgeItem }> => post('/v1/knowledge/link', body),
    addText: (body: { title: string; text: string; sourceName?: string; publishedAt?: string }): Promise<{ item: KnowledgeItem }> => post('/v1/knowledge/text', body),
    reprocess: (id: string): Promise<{ item: KnowledgeItem }> => post(`/v1/knowledge/items/${encodeURIComponent(id)}/reprocess`, {}),
    setStale: (id: string, stale: boolean): Promise<{ item: KnowledgeItem; snippets: KnowledgeSnippet[] }> => patch(`/v1/knowledge/items/${encodeURIComponent(id)}`, { stale }),
    remove: (id: string): Promise<{ ok: boolean }> => del(`/v1/knowledge/items/${encodeURIComponent(id)}`),
    updateSnippet: (id: string, body: { label?: string; text?: string; stale?: boolean }): Promise<{ snippet: KnowledgeSnippet }> => patch(`/v1/knowledge/snippets/${encodeURIComponent(id)}`, body),
    removeSnippet: (id: string): Promise<{ ok: boolean }> => del(`/v1/knowledge/snippets/${encodeURIComponent(id)}`),
    search: (q: string): Promise<{ results: Array<{ label: string; text: string; score: number }> }> => get(`/v1/knowledge/search?q=${encodeURIComponent(q)}`),
    scout: (): Promise<KnowledgeScoutState> => get('/v1/knowledge/scout'),
    runScout: (body: { sourceIds?: string[]; trends?: boolean } = {}): Promise<{ started: boolean }> => post('/v1/knowledge/scout/run', body),
  },
  agents: {
    list: (): Promise<Agent[]> => get('/v1/agents'),
    get: (agentId: string): Promise<Agent> => get(`/v1/agents/${agentId}`),
    /** Get current status of all agents (idle/working/researching) */
    status: (): Promise<Record<string, { status: 'idle' | 'working' | 'researching'; taskCount: number; lastHeartbeat: string | null }>> =>
      get('/v1/agents/status'),
    update: (agentId: string, data: Partial<Agent>): Promise<Agent> =>
      patch(`/v1/agents/${agentId}`, data),
    skills: (agentId: string): Promise<Skill[]> =>
      get(`/v1/agents/${agentId}/skills`),
    allSkills: (agentId: string): Promise<Skill[]> =>
      get(`/v1/agents/${agentId}/skills?includeInactive=true`),
    updateSkill: (agentId: string, skillId: string, data: { isActive?: boolean; name?: string; description?: string }): Promise<Skill> =>
      patch(`/v1/agents/${agentId}/skills/${skillId}`, data),
  },

  skills: {
    list: (): Promise<Skill[]> => get('/v1/skills'),
    get: (skillId: string): Promise<Skill> => get(`/v1/skills/${skillId}`),
    run: (
      skillId: string,
      briefData: Record<string, unknown>,
      brandId?: string,
    ): Promise<{ sessionId: string }> =>
      post(`/v1/skills/${skillId}/run`, { briefData, ...(brandId ? { brandId } : {}) }),
  },

  sessions: {
    list: (limit = 20, offset = 0): Promise<{
      sessions: SkillSession[];
      total: number;
    }> =>
      get(`/v1/sessions?limit=${limit}&offset=${offset}`),
    get: (sessionId: string): Promise<SkillSession> =>
      get(`/v1/sessions/${sessionId}`),
    streamUrl: (sessionId: string): string =>
      `${BASE_URL}/v1/sessions/${sessionId}/stream`,
  },

  deliverables: {
    /** One keyset-paginated page of everything the agents have produced. */
    list: (opts: { kind?: string; agentId?: string; brandId?: string; campaignId?: string; search?: string; cursor?: string; limit?: number } = {}): Promise<DeliverablesPage> => {
      const p = new URLSearchParams();
      if (opts.kind) p.set('kind', opts.kind);
      if (opts.agentId) p.set('agentId', opts.agentId);
      if (opts.brandId) p.set('brandId', opts.brandId);
      if (opts.campaignId) p.set('campaignId', opts.campaignId);
      if (opts.search) p.set('search', opts.search);
      if (opts.cursor) p.set('cursor', opts.cursor);
      if (opts.limit) p.set('limit', String(opts.limit));
      const q = p.toString();
      return get(`/v1/deliverables${q ? `?${q}` : ''}`);
    },
    archive: (id: string): Promise<{ archived: boolean }> =>
      post(`/v1/deliverables/${encodeURIComponent(id)}/archive`, {}),
  },

  campaignWork: {
    /** Everything the agents have done for one Beatly campaign. */
    get: (campaignId: string): Promise<CampaignWork> =>
      get(`/v1/campaign-work/${encodeURIComponent(campaignId)}`),
    /** Just the campaign's brand — cheap enough for the header badge. */
    brand: (campaignId: string): Promise<{ brandId?: string; brandName?: string; brandLogoUrl?: string }> =>
      get(`/v1/campaign-work/${encodeURIComponent(campaignId)}/brand`),
  },

  /** The decision queue: open gates across every campaign the user can see. */
  needsMe: {
    list: (): Promise<{ items: NeedsMeItem[] }> => get('/v1/needs-me'),
  },

  /** CM tooling — staff-gated server-side; 403 for everyone else. */
  cm: {
    me: (): Promise<{ staff: boolean; email: string | null }> => get('/v1/cm/me'),
    needsMe: (): Promise<CmNeedsMe> => get('/v1/cm/needs-me'),
    outbox: (status?: string): Promise<{ messages: CmOutboundMessage[] }> =>
      get(`/v1/cm/outbox${status ? `?status=${encodeURIComponent(status)}` : ''}`),
    outboxResolve: (id: string, action: 'approve' | 'dismiss'): Promise<{ message: CmOutboundMessage; note?: string }> =>
      post(`/v1/cm/outbox/${encodeURIComponent(id)}/${action}`, {}),
    draftReviews: (campaignId: string): Promise<{ reviews: CmDraftReview[] }> =>
      get(`/v1/cm/draft-reviews?campaignId=${encodeURIComponent(campaignId)}`),
    runDraftReviews: (campaignId: string, force = false): Promise<{ reviewed: number; skipped: number; capped: boolean; reviews: CmDraftReview[] }> =>
      post('/v1/cm/draft-reviews/run', { campaignId, force }),
    reconcile: (campaignId: string): Promise<CmReconciliation> =>
      get(`/v1/cm/reconcile/${encodeURIComponent(campaignId)}`),
    inbox: (): Promise<{ connected: boolean; conversations: CmConversation[]; note?: string }> =>
      get('/v1/cm/inbox'),
    inboxMessages: (id: number): Promise<{ messages: CmInboxMessage[] }> =>
      get(`/v1/cm/inbox/${id}/messages`),
    inboxReply: (id: number, body: string, title?: string): Promise<{ message: CmOutboundMessage; note?: string }> =>
      post(`/v1/cm/inbox/${id}/reply`, { body, title }),
    /** Can agents write to the Beatly back-office right now? (admin token health) */
    platformStatus: (force?: boolean): Promise<{ ok: boolean; source: 'env' | 'captured' | 'none'; error?: string; checkedAt: string; howToFix: string }> =>
      get(`/v1/cm/platform-status${force ? '?force=1' : ''}`),
    kickoffs: {
      list: (): Promise<{ sections: KickoffSection[]; kickoffs: Kickoff[] }> => get('/v1/cm/kickoffs'),
      create: (body: { clientName?: string; campaignName?: string; brandId?: string; fromPresentationId?: string }): Promise<{ kickoff: Kickoff }> =>
        post('/v1/cm/kickoffs', body),
      /** Proposals Lykke built for this team's clients — a kickoff can start from one. */
      proposals: (client?: string): Promise<{ proposals: KickoffProposal[] }> =>
        get(`/v1/cm/kickoffs/proposals${client ? `?client=${encodeURIComponent(client)}` : ''}`),
      /** Fill the kickoff from a proposal deck through the same quote-grounded importer as pasted material. */
      importProposal: (id: string, presentationId: string, overwrite?: boolean): Promise<KickoffImportResult & { proposal: KickoffProposal }> =>
        post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/import-proposal`, { presentationId, overwrite: !!overwrite }),
      get: (id: string): Promise<{ kickoff: Kickoff; sections: KickoffSection[]; sharing?: SharingInfo }> =>
        get(`/v1/cm/kickoffs/${encodeURIComponent(id)}`),
      /** Take ownership of a kickoff that has none. */
      claim: (id: string): Promise<{ kickoff: unknown; sharing?: SharingInfo }> => post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/claim`, {}),
      update: (id: string, body: { data?: Record<string, string | string[]>; campaignName?: string; brandId?: string; visibility?: SharingVisibility; sharedWith?: string[] }): Promise<{ kickoff: Kickoff }> =>
        patch(`/v1/cm/kickoffs/${encodeURIComponent(id)}`, body),
      prefill: (id: string): Promise<{ kickoff: Kickoff }> =>
        post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/prefill`, {}),
      /** Import a plan / brief / notes (text, PDF, images): only quoted values are written. */
      intake: async (id: string, data: { text?: string; files?: File[]; overwrite?: boolean }): Promise<KickoffImportResult> => {
        const fd = new FormData();
        if (data.text) fd.append('text', data.text);
        if (data.overwrite) fd.append('overwrite', 'true');
        for (const f of data.files || []) fd.append('files', f, f.name);
        const res = await fetch(`${API_BASE_URL}/v1/cm/kickoffs/${encodeURIComponent(id)}/intake`, { method: 'POST', headers: authHeaders(false), body: fd });
        if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Import failed (${res.status})`); }
        return res.json();
      },
      /** Names the platform shows on this client's campaigns: Beatly campaign managers + client-side contacts. */
      managers: (id: string): Promise<{ managers: string[]; clientManagers: string[] }> => get(`/v1/cm/kickoffs/${encodeURIComponent(id)}/managers`),
      /** Oneflow contracts / HubSpot deals, search-as-you-type. `configured:false` when the backend has no token. */
      lookups: (id: string, source: 'oneflow' | 'hubspot', q: string): Promise<{ configured: boolean; items: KickoffLookupItem[]; note?: string }> =>
        get(`/v1/cm/kickoffs/${encodeURIComponent(id)}/lookups?source=${source}&q=${encodeURIComponent(q)}`),
      finance: (id: string): Promise<KickoffFinance> => get(`/v1/cm/kickoffs/${encodeURIComponent(id)}/finance`),
      chat: (id: string, body: { message: string; history: Array<{ role: 'lykke' | 'user'; text: string }> }): Promise<KickoffChatResult> =>
        post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/chat`, body),
      realtimeSecret: (id: string): Promise<{ clientSecret: string; expiresAt: number; model: string; instructions: string; voice: string; userName: string | null; missing: string[] }> =>
        post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/realtime-secret`, {}),
      buildDraft: (id: string): Promise<{ taskId: string; name: string; note?: string; campaignId?: string; summary?: string }> =>
        post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/build-draft`, {}),
      publish: (id: string): Promise<{ taskId: string; name: string; note?: string; campaignId?: string; summary?: string }> =>
        post(`/v1/cm/kickoffs/${encodeURIComponent(id)}/publish`, {}),
    },
  },

  /** Wizard 2.0 — see cowork-cloud docs/WIZARD-2.0-DESIGN.md. */
  /** TikTok Business (Marketing API Discovery scope) — connect once, then Creative Center trends flow into proposals. */
  /** Discover → Competitor ads and TikTok trends (routes/insights). Pages open on the cache; refresh runs in the background. */
  insights: {
    adsLibrary: (p: { q?: string; pageId?: string; brandId?: string; market?: string; status?: 'active' | 'all'; media?: 'all' | 'video' | 'image'; page?: string | null; force?: boolean; platform?: 'meta' | 'tiktok' }): Promise<AdsLibraryResponse> => get(`/v1/insights/ads-library?${Object.entries({ q: p.q, pageId: p.pageId, brandId: p.brandId, market: p.market, status: p.status, media: p.media, page: p.page, force: p.force ? '1' : '', platform: p.platform }).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}`),
    keepAd: (p: { brandId?: string; market?: string; ad: LibraryAdRow; platform?: 'meta' | 'tiktok' }): Promise<{ asset: RadarAsset }> => post('/v1/insights/ads-library/keep', p),
    setKept: (id: string, kept: boolean): Promise<{ asset: RadarAsset }> => post(`/v1/insights/library/${id}/keep`, { kept }),
    library: (p: { brandId?: string; market?: string; kind?: string; advertiser?: string; kept?: boolean; format?: string; limit?: number; offset?: number }): Promise<RadarLibraryResponse> => get(`/v1/insights/library?${Object.entries({ ...p, kept: p.kept ? '1' : '' }).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}`),
    presence: (brandId?: string, market?: string): Promise<PresenceResponse> => get(`/v1/insights/presence?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    aiVisibility: (brandId?: string, market?: string): Promise<AiVisibilityResponse> => get(`/v1/insights/ai-visibility?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    handles: (brandId?: string, market?: string): Promise<HandleWatchResponse> => get(`/v1/insights/handles?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    creatorWatch: (brandId?: string, market?: string): Promise<CreatorWatchResponse> => get(`/v1/insights/creator-watch?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    canvas: (brandId?: string, planId?: string, boardId?: string): Promise<CanvasBoard> => get(`/v1/insights/canvas?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}${boardId ? `boardId=${encodeURIComponent(boardId)}` : planId ? `planId=${encodeURIComponent(planId)}` : ''}`),
    canvasBoards: (brandId?: string): Promise<{ boards: CanvasBoardMeta[] }> => get(`/v1/insights/canvas/boards?brandId=${encodeURIComponent(brandId || 'default')}`),
    createCanvasBoard: (body: { brandId?: string; name: string; planId?: string | null }): Promise<{ board: CanvasBoardMeta }> => post('/v1/insights/canvas/boards', body),
    renameCanvasBoard: (id: string, body: { brandId?: string; name: string }): Promise<{ ok: boolean }> => patch(`/v1/insights/canvas/boards/${encodeURIComponent(id)}`, body),
    deleteCanvasBoard: (id: string, brandId?: string): Promise<{ ok: boolean }> => del(`/v1/insights/canvas/boards/${encodeURIComponent(id)}?brandId=${encodeURIComponent(brandId || 'default')}`),
    canvasCatalogue: (brandId?: string, market?: string): Promise<CanvasCatalogue> => get(`/v1/insights/canvas/catalogue?brandId=${encodeURIComponent(brandId || 'default')}&market=${encodeURIComponent(market || 'Sweden')}`),
    canvasPick: (body: { brandId?: string; boardId: string; kind: PickKind; ref: string; market?: string; duplicate?: boolean }): Promise<{ pick: { id: string } }> => post('/v1/insights/canvas/picks', body),
    removeCanvasPick: (id: string, brandId: string | undefined, boardId: string): Promise<{ ok: boolean }> => del(`/v1/insights/canvas/picks/${encodeURIComponent(id)}?brandId=${encodeURIComponent(brandId || 'default')}&boardId=${encodeURIComponent(boardId)}`),
    canvasRevert: (id: string, body: { brandId?: string; boardId: string }): Promise<{ node: { id: string } | null }> => post(`/v1/insights/canvas/nodes/${encodeURIComponent(id)}/revert`, body),
    canvasBatch: (body: { brandId?: string; boardId: string; tool: 'brief' | 'variations' | 'storyboard' | 'ask' | 'image'; sharedIds: string[]; rowIds: string[]; text?: string; parentId: string; format?: '9:16' | '1:1'; engine?: ImageEngine }): Promise<{ started: number }> => post('/v1/insights/canvas/batch', body),
    canvasComment: (id: string, body: { brandId?: string; boardId: string; text?: string; resolve?: string; reopen?: string }): Promise<{ comments: Array<{ id: string; by: string; text: string; at: string; resolved?: boolean }> }> => post(`/v1/insights/canvas/nodes/${encodeURIComponent(id)}/comments`, body),
    canvasApprove: (id: string, body: { brandId?: string; boardId: string; approved: boolean }): Promise<{ node: { id: string } }> => post(`/v1/insights/canvas/nodes/${encodeURIComponent(id)}/approve`, body),
    canvasUploadAssets: async (brandId: string | undefined, boardId: string, files: File[]): Promise<{ made: string[] }> => { const fd = new FormData(); fd.append('brandId', brandId || 'default'); fd.append('boardId', boardId); for (const f of files) fd.append('files', f); const r = await fetch(`${BASE_URL}/v1/insights/canvas/assets`, { method: 'POST', headers: authHeaders(false), body: fd }); if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `Upload failed (${r.status})`); return r.json(); },
    canvasTemplates: (): Promise<{ templates: Array<{ id: string; name: string; description: string; createdAt: string; cards: number }> }> => get('/v1/insights/canvas/templates'),
    saveCanvasTemplate: (body: { brandId?: string; boardId: string; name: string; description?: string }): Promise<{ template: { id: string; name: string } }> => post('/v1/insights/canvas/templates', body),
    deleteCanvasTemplate: (id: string): Promise<{ ok: boolean }> => del(`/v1/insights/canvas/templates/${encodeURIComponent(id)}`),
    useCanvasTemplate: (id: string, body: { brandId?: string; name?: string }): Promise<{ board: CanvasBoardMeta; placed: number; wires: number }> => post(`/v1/insights/canvas/templates/${encodeURIComponent(id)}/use`, body),
    canvasExportDeck: (body: { brandId?: string; boardId: string; ids: string[]; title?: string }): Promise<{ presentationId: string; title: string; slides: number }> => post('/v1/insights/canvas/export/deck', body),
    canvasAdsCsv: async (brandId: string | undefined, boardId: string, ids: string[]): Promise<{ blob: Blob; rows: number }> => { const r = await fetch(`${BASE_URL}/v1/insights/canvas/export/ads.csv?brandId=${encodeURIComponent(brandId || 'default')}&boardId=${encodeURIComponent(boardId)}&ids=${encodeURIComponent(ids.join(','))}`, { headers: authHeaders(false) }); if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `Export failed (${r.status})`); return { blob: await r.blob(), rows: Number(r.headers.get('X-Rows') || 0) }; },
    canvasBrief: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string; instruction?: string }): Promise<{ node: { id: string } }> => post('/v1/insights/canvas/brief', body),
    saveCanvasLayout: (body: { brandId?: string; boardId: string; positions: Record<string, { x: number; y: number }> | null; edges?: Array<{ from: string; to: string }>; hidden?: string[] }): Promise<{ ok: boolean }> => put('/v1/insights/canvas/layout', body),
    canvasNode: (body: { brandId?: string; boardId: string; node: StudioNodeInput & { imageUrl?: string | null; videoUrl?: string | null; sourceImageUrl?: string | null; format?: string } }): Promise<{ node: StudioNodeInput & { id: string } }> => post('/v1/insights/canvas/nodes', body),
    removeCanvasNode: (id: string, brandId: string | undefined, boardId: string): Promise<{ ok: boolean }> => del(`/v1/insights/canvas/nodes/${encodeURIComponent(id)}?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}boardId=${encodeURIComponent(boardId)}`),
    canvasAsk: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string; question: string }): Promise<{ answer: string }> => post('/v1/insights/canvas/ask', body),
    canvasVariations: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string; count?: number }): Promise<{ base: string | null; variants: CanvasVariant[] }> => post('/v1/insights/canvas/variations', body),
    canvasBrandCheck: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string }): Promise<{ results: Array<{ id: string; title: string; pass: boolean; notes: string[] }> }> => post('/v1/insights/canvas/brand-check', body),
    canvasScenarios: (body: { planId: string; budgets?: number[] }): Promise<CanvasScenarios> => post('/v1/insights/canvas/scenarios', body),
    canvasKeepScenario: (body: { planId: string; budget: number; multiplier?: number }): Promise<{ plan: MediaPlan }> => post('/v1/insights/canvas/scenarios/keep', body),
    canvasDirection: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string }): Promise<{ node: { id: string } }> => post('/v1/insights/canvas/direction', body),
    canvasStoryboard: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string }): Promise<{ node: { id: string } }> => post('/v1/insights/canvas/storyboard', body),
    canvasChart: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string; parentId?: string }): Promise<{ nodes: Array<{ id: string; title: string }>; skipped: string[] }> => post('/v1/insights/canvas/chart', body),
    canvasCreative: (body: { brandId?: string; boardId: string; text: string; parentId?: string; format: '9:16' | '1:1' | '4:5' | '16:9'; video?: boolean; engine?: ImageEngine; count?: number; replaceId?: string }): Promise<{ node: { id: string }; nodes: Array<{ id: string }> }> => post('/v1/insights/canvas/creative', body),
    canvasConcepts: (body: { brandId?: string; boardId: string; ids: string[]; upstreamOf?: string; instruction?: string; count?: number; parentId?: string }): Promise<{ nodes: Array<{ id: string; title: string }> }> => post('/v1/insights/canvas/concepts', body),
    canvasFlows: (): Promise<{ flows: Array<{ id: string; name: string; description: string; steps: number }> }> => get('/v1/insights/canvas/flows'),
    useCanvasFlow: (id: string, body: { brandId?: string; name?: string }): Promise<{ board: CanvasBoardMeta; placed: number; wires: number }> => post(`/v1/insights/canvas/flows/${encodeURIComponent(id)}/use`, body),
    canvasClip: (body: { brandId?: string; boardId: string; id: string }): Promise<{ node: { id: string } }> => post('/v1/insights/canvas/clip', body),
    canvasCapture: (body: { brandId?: string; boardId: string; turns: Array<{ role: 'agent' | 'user'; text: string }> }): Promise<{ placed: Array<{ id: string; kind: string; title: string }> }> => post('/v1/insights/canvas/capture', body),
    canvasIntoProposal: (body: { brandId?: string; boardId: string; ids: string[] }): Promise<{ presentationId: string; title: string; added: number }> => post('/v1/insights/canvas/into-proposal', body),
    canvasRealtimeSecret: (body: { brandId?: string; boardId: string }): Promise<{ clientSecret: string; expiresAt: number; model: string; instructions: string; voice: string; userName: string | null }> => post('/v1/insights/canvas/realtime-secret', body),
    higgsfieldStatus: (): Promise<{ connected: boolean; connectedAt?: string; expiresAt?: number; canRefresh?: boolean; apiKeys: boolean; redirectUri: string; engines?: { gemini: boolean; higgsfield: boolean } }> => get('/v1/integrations/higgsfield/status'),
    higgsfieldConnectUrl: (back: string) => { const token = getTokenFromStorage(); return `${BASE_URL}/v1/integrations/higgsfield/connect?back=${encodeURIComponent(back)}${token ? `&token=${token}` : ''}`; },
    higgsfieldDisconnect: (): Promise<{ ok: boolean }> => del('/v1/integrations/higgsfield'),
    commentPulse: (brandId?: string, market?: string, force?: boolean): Promise<CommentPulseResponse> => get(`/v1/insights/comment-pulse?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}${force ? '&force=1' : ''}`),
    demand: (brandId?: string, market?: string): Promise<DemandResponse> => get(`/v1/insights/demand?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    pressure: (brandId?: string, market?: string): Promise<PressureResponse> => get(`/v1/insights/pressure?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    competitorAds: (brandId?: string, market?: string): Promise<CompetitorAdsResponse> => get(`/v1/insights/competitor-ads?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}`),
    refreshCompetitorAds: (brandId: string | undefined, market: string, names?: string[]): Promise<{ started: boolean; refreshing: boolean; names?: string[]; note?: string }> => post(`/v1/insights/competitor-ads/refresh${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, { market, names }),
    adSnapshot: (adId: string): Promise<{ url: string }> => get(`/v1/insights/ad-snapshot/${encodeURIComponent(adId)}`),
    tiktokTrends: (brandId?: string, market?: string, category?: string, range?: string, force?: boolean): Promise<TikTokTrendsResponse> => get(`/v1/insights/tiktok-trends?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}market=${encodeURIComponent(market || 'Sweden')}${category ? `&category=${encodeURIComponent(category)}` : ''}${range ? `&range=${range}` : ''}${force ? '&force=1' : ''}`),
  },

  tiktok: {
    status: (): Promise<{ configured: boolean; connected: boolean; advertisers: number; obtainedAt: string | null; redirectUri: string }> => get('/v1/tiktok/status'),
    connectUrl: (): Promise<{ url: string; redirectUri: string }> => get('/v1/tiktok/connect-url'),
    disconnect: (): Promise<{ ok: boolean }> => post('/v1/tiktok/disconnect', {}),
  },

  /** Complete proposals — the planner rebuilt slide by slide (see cowork/proposals). */
  proposals: {
    prepare: (brandId?: string): Promise<ProposalPrepare> => get(`/v1/proposals/prepare${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    step: <T = any>(body: { step: ProposalStepId; brandId?: string; brandName?: string; intent: ProposalIntent; state?: Record<string, unknown>; brief?: string }): Promise<{ step: ProposalStepId; data: T }> =>
      post('/v1/proposals/step', body),
    visuals: (body: { key?: string; items: Array<{ key: string; prompt: string; aspect?: '9:16' | '4:5' | '1:1' | '16:9' }> }): Promise<{ items: Array<{ key: string; url: string; aspect: string }>; configured: boolean }> =>
      post('/v1/proposals/visuals', body),
    /** Speech-to-speech call: short-lived OpenAI Realtime client secret (our key stays server-side). */
    realtimeSecret: (body: { brandId?: string; brandName?: string; language?: string; intent?: Partial<ProposalIntent> }): Promise<{ clientSecret: string; expiresAt: number; model: string; instructions?: string; voice?: string }> =>
      post('/v1/proposals/realtime-secret', body),
    /** Live competitor signals: Meta Ad Library + TikTok pulse per competitor (Apify, cached 24 h). */
    intel: (body: { competitors: string[]; market?: string; brandName?: string; hint?: string }): Promise<{ items: CompetitorIntel[]; trends?: TikTokTrends | null; configured: boolean; tiktokConnected?: boolean }> =>
      post('/v1/proposals/intel', body),
    /** Call mode: one conversational turn — Kai's next line, extracted fields, a change instruction, advance/done flags. */
    converse: (body: { stage: 'setup' | ProposalStepId; transcript?: string; history?: Array<{ role: 'kai' | 'user'; text: string }>; intent?: Partial<ProposalIntent>; brandId?: string; brandName?: string; stepData?: unknown; brief?: string }): Promise<{ say: string; patch: Partial<ProposalIntent> & { brandName?: string }; instruction: string | null; advance: boolean; done: boolean }> =>
      post('/v1/proposals/converse', body),
    /** Free-text edit of one step: Kai rewrites the step's data from the instruction. */
    revise: <T = any>(body: { step: 'setup' | ProposalStepId; instruction: string; data?: unknown; intent?: ProposalIntent; brandId?: string; brandName?: string; brief?: string }): Promise<{ step: string; data: T; note: string }> =>
      post('/v1/proposals/revise', body),
    /** Translate a built deck's copy into a language (structure, numbers, names untouched). */
    translate: (body: { slides: Array<Record<string, unknown>>; language: string }): Promise<{ slides: Array<Record<string, unknown>>; translated: number }> =>
      post('/v1/proposals/translate', body),
    /** Pre-step: paste text and/or drop files (text, PDF, images) — Kai pre-fills the setup. */
    intake: async (data: { text?: string; files?: File[]; brandId?: string; brandName?: string }): Promise<{ intent: Partial<ProposalIntent>; brandName: string | null; summary: string; confidence: Record<string, number>; sourceChars: number; images: number; notes: string[]; sourceText: string }> => {
      const fd = new FormData();
      if (data.text) fd.append('text', data.text);
      if (data.brandId) fd.append('brandId', data.brandId);
      if (data.brandName) fd.append('brandName', data.brandName);
      for (const f of data.files || []) fd.append('files', f, f.name);
      const headers = authHeaders(false); // the token AND the client (X-Cowork-Team) — never the house by default
      const res = await fetch(`${BASE_URL}/v1/proposals/intake`, { method: 'POST', headers, body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Intake failed (${res.status})`);
      return json;
    },
  },

  wizard: {
    prepare: (): Promise<WizardPrepare> => get('/v1/wizard/prepare'),
    generatePlan: (body: { brandId: string; brandName?: string; intent: WizardIntent }): Promise<{ plan: WizardPlan }> =>
      post('/v1/wizard/generate-plan', body),
    handoff: (body: { brandId: string; brandName?: string; intent: WizardIntent; plan: WizardPlan; campaignName?: string }): Promise<{ taskId: string; name: string }> =>
      post('/v1/wizard/handoff', body),
  },

  brands: {
    /** List all brands for the authenticated team (local + Omni Ad API) */
    list: (): Promise<{ brands: BrandListItem[]; defaultBrandId?: string | null }> =>
      get('/v1/brands'),
    get: (brandId?: string): Promise<BrandGraph> =>
      get(`/v1/brands/me${brandId ? `?brandId=${brandId}` : ''}`),
    update: (graph: Partial<BrandGraph>, brandId?: string): Promise<BrandGraph> =>
      put(`/v1/brands/me${brandId ? `?brandId=${brandId}` : ''}`, graph),
    crawl: (url: string, opts?: { shallow?: boolean; maxPages?: number }): Promise<{
      // Identity + voice (always present)
      name: string; industry: string; description: string; usp: string;
      voiceTone: string; voiceAttributes: string[]; writingStyle: string;
      keyMessages: string[]; targetStatement: string; website: string;
      socialAccounts?: Record<string, string>;
      // Deep-crawl additions (present unless shallow=true)
      visualStyle?: { primaryColors?: string[]; fonts?: string[]; designLanguage?: string };
      audience?: {
        segments?: Array<{ name: string; description: string; motivations?: string[] }>;
        primaryPersona?: {
          name?: string; occupation?: string; bio?: string;
          goals?: string[]; challenges?: string[]; favoritePlatforms?: string[];
        };
      };
      content?: {
        contentPillars?: string[];
        topPerformingFormats?: string[];
        topPerformingTopics?: string[];
      };
      competitors?: Array<{ name: string; website?: string; positioning?: string }>;
      pagesFetched?: string[];
    }> => post('/v1/brands/crawl', { url, ...(opts || {}) }),

    /**
     * Build the whole Brand OS from a website — identity, voice, audience,
     * content and competitors. Returns as soon as the job starts; the work
     * continues server-side, so leaving the page doesn't cancel it.
     * A site other than the one the Brand OS was built from answers 409 `site_changed` (see siteChangeOf) until
     * the caller confirms with `startOver: true`, which replaces the Brand OS with what the new site gives.
     */
    autofill: (url: string, brandId?: string, opts?: { startOver?: boolean }): Promise<AutofillJob> =>
      post('/v1/brands/autofill', { url, brandId, ...(opts?.startOver ? { startOver: true } : {}) }),

    /** Read the brand's look from its website again (faces, colours, buttons, art direction). */
    look: (brandId?: string, url?: string): Promise<{ look: import('../types/cowork').BrandLook; set: string[]; brand: import('../types/cowork').BrandGraph['brand'] }> =>
      post('/v1/brands/look', { brandId, url }),

    /** Progress of the latest Brand OS build for this brand. */
    autofillStatus: (brandId?: string): Promise<AutofillJob | { status: 'idle' }> =>
      get(`/v1/brands/autofill-status${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),

    /** Fetch rich brand data (logos, colors, fonts) from Brandfetch and merge into BrandGraph */
    brandfetch: (domain: string, brandId?: string): Promise<{
      success: boolean;
      brandfetchData: unknown;
      updatedGraph: BrandGraph;
    }> => post('/v1/brands/brandfetch', { domain, brandId }),

    /** Discover products from a brand's website using Claude */
    discoverProducts: (domain: string, brandId?: string, offset?: number): Promise<{
      products: Array<{ id: string; name: string; description: string; price?: string; currency?: string; imageUrl?: string; url?: string; category?: string }>;
      hasMore: boolean;
    }> => post('/v1/brands/discover-products', { domain, brandId, offset }),

    uploadAsset: (file: File, slot: string, brandId?: string): Promise<{ url: string }> => {
      const formData = new FormData();
      formData.append('asset', file);
      formData.append('slot', slot);
      if (brandId) formData.append('brandId', brandId);
      const headers = authHeaders(false); // the token AND the client (X-Cowork-Team) — never the house by default
      return fetch(`${BASE_URL}/v1/brands/upload-asset`, { method: 'POST', headers, body: formData })
        .then(async res => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
          }
          return res.json();
        });
    },

    getDiscoveryStatus: (brandId?: string): Promise<{
      status: 'idle' | 'running' | 'done' | 'error';
      nodesCount?: number;
      startedAt?: string;
      completedAt?: string;
    }> => get(`/v1/brands/discovery-status${brandId ? `?brandId=${brandId}` : ''}`),

    discoverCompetitors: (
      onEvent: (event: { type: string; message?: string; data?: unknown }) => void,
      brandId?: string,
    ): Promise<void> => {
      return fetch(`${BASE_URL}/v1/brands/discover-competitors`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(brandId ? { brandId } : {}),
      }).then(async res => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
        }
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (line.startsWith('data: ') && line !== 'data: [DONE]') {
              try {
                onEvent(JSON.parse(line.slice(6)));
              } catch { /* ignore malformed lines */ }
            }
          }
        }
      });
    },

    /** Brand OS Pulse — completeness per section, gaps, live signals from the platform. */
    pulse: (brandId?: string): Promise<BrandPulse> => get(`/v1/brands/pulse${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    generateAudiences: (brandId?: string, keepExisting = true): Promise<{ segments: AudienceSegment[]; graph: BrandGraph }> => post(`/v1/brands/generate-audiences${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, { keepExisting }),
    /** Size one audience on Meta (interest search + reach estimate) and store it on the segment. */
    estimateAudience: (segmentId: string, brandId?: string, market?: string): Promise<{ segment: AudienceSegment; graph: BrandGraph }> => post(`/v1/brands/audiences/${encodeURIComponent(segmentId)}/estimate?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}${market ? `market=${encodeURIComponent(market)}` : ''}`, {}),
    marketBrief: (brandId?: string, market?: string): Promise<{ brief: MarketBrief | null; fresh: boolean; briefs?: Array<MarketBrief & { fresh: boolean }> }> => get(`/v1/brands/market-brief?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}${market ? `market=${encodeURIComponent(market)}` : ''}`),
    refreshMarketBrief: (brandId: string | undefined, market: string): Promise<{ brief: MarketBrief; fresh: boolean }> => post(`/v1/brands/market-brief/refresh${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, { market }),
    estimateAllAudiences: (brandId?: string, market?: string): Promise<{ graph: BrandGraph; errors: Record<string, string> }> => post(`/v1/brands/audiences/estimate-all?${brandId ? `brandId=${encodeURIComponent(brandId)}&` : ''}${market ? `market=${encodeURIComponent(market)}` : ''}`, {}),
    /** Meta's interest taxonomy from the local file — the vocabulary audiences are defined against. */
    listInterests: (q: string, limit = 15): Promise<{ fetchedAt: string; interests: Array<{ id: string; name: string; path: string[]; lower: number | null; upper: number | null; main: boolean }> }> => get(`/v1/brands/interests/list?q=${encodeURIComponent(q)}&limit=${limit}`),
    /** A promotable joins its primary audience (new audience only when buyer/occasion differ). */
    assignPromotable: (promotableId: string, brandId?: string): Promise<{ assignedTo: string; created: boolean; reason: string; graph: BrandGraph }> => post(`/v1/brands/promotables/${encodeURIComponent(promotableId)}/assign-audience${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, {}),
    /** Hero portrait for one audience (Gemini image model), stored as portraitUrl. */
    audiencePortrait: (segmentId: string, brandId?: string): Promise<{ segment: AudienceSegment; graph: BrandGraph }> => post(`/v1/brands/audiences/${encodeURIComponent(segmentId)}/portrait${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, {}),
    audiencePortraitsAll: (brandId?: string, missingOnly = true): Promise<{ graph: BrandGraph; errors: Record<string, string> }> => post(`/v1/brands/audiences/portraits-all${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, { missingOnly }),
    searchInterests: (q: string): Promise<{ interests: Array<{ id: string; name: string; audienceLower: number | null; audienceUpper: number | null; path?: string[] }> }> => get(`/v1/brands/interests/search?q=${encodeURIComponent(q)}`),
    profileCompetitor: (id: string, brandId?: string): Promise<{ competitor: Competitor; graph: BrandGraph }> => post(`/v1/brands/profile-competitor${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, { id }),
    library: {
      list: (brandId?: string): Promise<{ files: LibraryFile[] }> => get(`/v1/brands/library${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
      upload: async (files: File[], brandId?: string): Promise<{ files: LibraryFile[] }> => {
        const fd = new FormData(); for (const f of files) fd.append('files', f, f.name);
        const res = await fetch(`${API_BASE_URL}/v1/brands/library${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, { method: 'POST', headers: authHeaders(false), body: fd });
        if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Upload failed (${res.status})`); }
        return res.json();
      },
      remove: (id: string): Promise<{ ok: boolean }> => del(`/v1/brands/library/${encodeURIComponent(id)}`),
      reprocess: (id: string, brandId?: string): Promise<{ file: LibraryFile }> => post(`/v1/brands/library/${encodeURIComponent(id)}/reprocess${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, {}),
      search: (q: string, brandId?: string): Promise<{ hits: Array<{ fileId: string; file: string; idx: number; text: string; score: number }> }> => get(`/v1/brands/library/search?q=${encodeURIComponent(q)}${brandId ? `&brandId=${encodeURIComponent(brandId)}` : ''}`),
    },
    uploadPdf: (file: File): Promise<{
      name: string; industry: string; description: string; usp: string;
      voiceTone: string; voiceAttributes: string[]; writingStyle: string;
      thingsToAvoid: string[]; keyMessages: string[]; targetStatement: string;
      primaryColors: string[]; fonts: string[];
    }> => {
      const formData = new FormData();
      formData.append('pdf', file);
      const headers = authHeaders(false); // the token AND the client (X-Cowork-Team) — never the house by default
      return fetch(`${BASE_URL}/v1/brands/upload-pdf`, { method: 'POST', headers, body: formData })
        .then(async res => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
          }
          return res.json();
        });
    },
  },

  contentQueue: {
    list: (status?: string): Promise<{
      teamId: string;
      items: Array<{
        id: string; teamId: string; agentId: string; platform: string;
        title: string; body: string; hashtags: string[]; visualDirection?: string;
        postType: string; status: string; editedBody?: string;
        scheduledAt?: string; publishedAt?: string; createdAt: string;
      }>;
    }> => get(`/v1/content-queue${status ? `?status=${status}` : ''}`),
    update: (id: string, data: { editedBody?: string; status?: string; scheduledAt?: string }): Promise<unknown> =>
      patch(`/v1/content-queue/${id}`, data),
    delete: (id: string): Promise<{ deleted: boolean }> =>
      del(`/v1/content-queue/${id}`),
    publish: (id: string): Promise<{
      published?: boolean;
      externalId?: string;
      externalUrl?: string;
      error?: string;
    }> => post(`/v1/content-queue/${id}/publish`, {}),
    generateVisual: (id: string, visualDirection?: string): Promise<{
      imageUrl?: string;
      error?: string;
    }> => post(`/v1/content-queue/${id}/generate-visual`, { visualDirection }),
  },

  integrations: {
    list: (): Promise<{
      integrations: Array<{
        id: string; teamId: string; userId: string; provider: string;
        profileName?: string; profileUrl?: string; profileImage?: string; createdAt: string;
      }>;
    }> => get('/v1/integrations'),
    connectUrl: () => {
      const token = getTokenFromStorage();
      return `${BASE_URL}/v1/integrations/linkedin/connect${token ? `?token=${token}` : ''}`;
    },
    googleAdsConnectUrl: (brandId?: string) => {
      const token = getTokenFromStorage();
      return `${BASE_URL}/v1/integrations/google-ads/connect?brandId=${brandId || 'default'}${token ? `&token=${token}` : ''}`;
    },
    googleAdsAccounts: (): Promise<{ accounts: Array<{ customerId: string; descriptiveName: string }> }> =>
      get('/v1/integrations/google-ads/accounts'),
    googleAdsSelectAccount: (customerId: string, descriptiveName?: string): Promise<{ ok: boolean }> =>
      post('/v1/integrations/google-ads/select-account', { customerId, descriptiveName }),
    linkedinAdAccounts: (): Promise<{ accounts: Array<{ adAccountId: string; name: string; status: string; currency: string }> }> =>
      get('/v1/integrations/linkedin/ad-accounts'),
    linkedinSelectAdAccount: (adAccountId: string, name?: string): Promise<{ ok: boolean }> =>
      post('/v1/integrations/linkedin/select-ad-account', { adAccountId, name }),
    delete: (id: string): Promise<{ deleted: boolean }> =>
      del(`/v1/integrations/${id}`),
  },

  artifacts: {
    list: (): Promise<{ artifacts: Artifact[] }> =>
      get('/v1/artifacts'),
    get: (artifactId: string): Promise<Artifact> =>
      get(`/v1/artifacts/${artifactId}`),
    generate: (
      sessionId: string,
    ): Promise<{ artifactId: string; cached: boolean }> =>
      post('/v1/artifacts', { sessionId }),
    delete: (artifactId: string): Promise<{ deleted: boolean }> =>
      del(`/v1/artifacts/${artifactId}`),
  },

  googleAdsAgent: {
    run: (
      task: string,
      onEvent: (event: GoogleAdsAgentEvent) => void,
      history: unknown[] = [],
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetch(`${BASE_URL}/v1/google-ads/run`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ task, history }),
        }).then(async res => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            return reject(new Error((err as { error?: string }).error || `Request failed: ${res.status}`));
          }
          const reader = res.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  onEvent(data as GoogleAdsAgentEvent);
                } catch { /* ignore */ }
              }
            }
          }
          resolve();
        }).catch(reject);
      });
    },
  },

  seoAgent: {
    run: (task: string, onEvent: (event: SeoAgentEvent) => void): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetch(`${BASE_URL}/v1/seo-agent/run`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ task }),
        }).then(async res => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            return reject(new Error((err as { error?: string }).error || `Request failed: ${res.status}`));
          }
          const reader = res.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  onEvent(data as SeoAgentEvent);
                } catch { /* ignore parse errors */ }
              }
            }
          }
          resolve();
        }).catch(reject);
      });
    },
  },

  calls: {
    /** Short-lived key for a transcription-only Realtime session (the room's ears when Lykke sits in on a meeting). */
    earsSecret: (language?: string): Promise<{ clientSecret: string; expiresAt?: number }> =>
      post('/v1/calls/ears-secret', language ? { language } : {}),
    /** Keep a live agent call in full (internal wiki page + call tables) and mine it for Brand OS insights. */
    archive: (body: { kind: 'presentation' | 'kickoff' | 'proposal' | 'meeting'; agent: string; title: string; brandId?: string; refId?: string | null; userName?: string | null; startedAt?: string; endedAt?: string; turns: Array<{ role: 'agent' | 'user' | 'room'; text: string; at?: string }>; context?: string }): Promise<{ id: string; slug: string; turns: number; insights: { summary: string; insights: Array<{ text: string; kind: string; confidence: string }>; followUps: string[]; sentiment: string } | null }> =>
      post('/v1/calls/archive', body),
    start: (
      agentIds: string[],
    ): Promise<{
      callSessionId: string;
      teamId: string;
      startedAt: string;
      participants: Array<{
        id: string; name: string; title: string;
        avatarEmoji: string; avatarUrl?: string; accentColor: string;
      }>;
      wsUrl: string;
      stt: { provider: string; language: string; continuous: boolean; interimResults: boolean };
    }> => post('/v1/calls/start', { agentIds }),

    end: (callSessionId: string): Promise<{
      callSessionId: string;
      status: string;
      durationSeconds: number;
      chatSessionId: string;
      turnCount: number;
    }> => post(`/v1/calls/${callSessionId}/end`, {}),

    utterance: (callSessionId: string, text: string): Promise<{ status: string }> =>
      post(`/v1/calls/${callSessionId}/utterance`, { text }),

    list: (): Promise<{
      teamId: string;
      sessions: Array<{
        id: string; teamId: string; agentIds: string[];
        status: string; startedAt: string; endedAt?: string;
        durationSeconds?: number; turnCount: number;
      }>;
    }> => get('/v1/calls'),

    get: (callSessionId: string): Promise<{
      session: {
        id: string; teamId: string; agentIds: string[];
        status: string; startedAt: string; turnCount: number;
      };
      turns: Array<{
        id: string; speakerRole: string; speakerName: string;
        textContent: string; audioUrl?: string; turnIndex: number;
        agentInfo?: { id: string; name: string; avatarEmoji: string; accentColor: string } | null;
      }>;
    }> => get(`/v1/calls/${callSessionId}`),

    sttConfig: (): Promise<{
      provider: string; language: string; continuous: boolean; interimResults: boolean;
    }> => get('/v1/calls/stt-config'),

    wsUrl: () => {
      const token = getTokenFromStorage();
      return `${BASE_URL.replace(/^http/, 'ws')}/ws${token ? `?token=${token}` : ''}`;
    },
  },

  tasks: {
    list: (status?: string, agentId?: string, opts?: { chatSessionId?: string; projectId?: string }): Promise<{ teamId: string; tasks: Task[] }> => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (agentId) params.set('assignedAgentId', agentId);
      if (opts?.chatSessionId) params.set('chatSessionId', opts.chatSessionId);
      if (opts?.projectId) params.set('projectId', opts.projectId);
      const qs = params.toString();
      return get(`/v1/tasks${qs ? `?${qs}` : ''}`);
    },
    get: (taskId: string): Promise<{
      task: Task;
      subtasks: Task[];
      deliverables: TaskDeliverable[];
      attachments: TaskAttachment[];
      comments: TaskComment[];
    }> => get(`/v1/tasks/${taskId}`),
    create: (data: {
      title: string; description?: string;
      assignedAgentId: string; priority?: string; deadline?: string;
      parentTaskId?: string; brandId?: string;
    }): Promise<Task> => post('/v1/tasks', data),
    update: (taskId: string, data: {
      title?: string; description?: string; status?: string;
      priority?: string; assignedAgentId?: string; deadline?: string;
    }): Promise<Task> =>
      patch(`/v1/tasks/${taskId}`, data),
    delete: (taskId: string): Promise<{ deleted: boolean }> => del(`/v1/tasks/${taskId}`),
    execute: (taskId: string): Promise<{ status: string; taskId: string }> =>
      post(`/v1/tasks/${taskId}/execute`, {}),
    uploadAttachment: (taskId: string, file: File): Promise<TaskAttachment> => {
      const formData = new FormData();
      formData.append('file', file);
      const headers = authHeaders(false); // the token AND the client (X-Cowork-Team) — never the house by default
      return fetch(`${BASE_URL}/v1/tasks/${taskId}/attachments`, { method: 'POST', headers, body: formData })
        .then(async res => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }));
            throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`);
          }
          return res.json();
        });
    },
    addComment: (taskId: string, content: string, role?: string): Promise<TaskComment> =>
      post(`/v1/tasks/${taskId}/comments`, { content, ...(role ? { role } : {}) }),
    triggerReview: (taskId: string, brandId?: string): Promise<{ status: string }> =>
      post(`/v1/tasks/${taskId}/review`, { brandId }),
    mentionAgents: (taskId: string, agentIds: string[], brandId?: string): Promise<{ status: string }> =>
      post(`/v1/tasks/${taskId}/mention`, { agentIds, brandId }),
    markSeen: (taskId: string): Promise<{ seen: boolean }> =>
      post(`/v1/tasks/${taskId}/seen`, {}),
    getSeen: (taskId: string): Promise<{ seen: Array<{ userId: string; agentId: string | null; agent: { id: string; name: string; avatarEmoji: string } | null; seenAt: string }> }> =>
      get(`/v1/tasks/${taskId}/seen`),
  },

  tts: {
    generate: (agentId: string, text: string): Promise<{ audioUrl: string }> =>
      post('/v1/tts', { agentId, text }),
    audioSrc: (audioUrl: string) => `${BASE_URL}${audioUrl}`,
    prefill: (agentIds: string[]): Promise<{
      fillers: Record<string, { question: string[]; statement: string[]; generic: string[] }>
    }> => post('/v1/tts/prefill', { agentIds }),
  },

  callRouting: {
    route: (
      message: string,
      agentIds: string[],
      history?: Array<{ role: string; agentId?: string; agentName?: string; content: string }>
    ): Promise<{ primaryAgentId: string; secondaryAgentId: string | null; reasoning: string }> =>
      post('/v1/calls/route', { message, agentIds, history }),
  },

  chats: {
    list: (limit = 50): Promise<{
      teamId: string;
      sessions: Array<{
        id: string; agentId: string; agentName: string; agentEmoji: string; agentColor: string;
        title: string | null; startedAt: string; lastMessageAt: string; messageCount: number;
      }>;
    }> => get(`/v1/chats?limit=${limit}`),

    get: (agentId: string, sessionId: string): Promise<{
      session: { id: string; agentId: string; title: string | null; startedAt: string; lastMessageAt: string; messageCount: number };
      messages: Array<{ id: string; sessionId: string; role: 'user' | 'assistant'; content: string; createdAt: string }>;
    }> => get(`/v1/agents/${agentId}/chats/${sessionId}`),
  },

  hooks: {
    list: (limit = 50, event?: string, agentId?: string): Promise<{
      count: number;
      events: Array<{
        event: string; timestamp: string; teamId: string; agentId: string; agentName?: string;
        sessionId?: string; toolName?: string; toolDurationMs?: number;
        taskId?: string; taskTitle?: string; tokensIn?: number; tokensOut?: number; error?: string;
      }>;
    }> => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (event) params.set('event', event);
      if (agentId) params.set('agentId', agentId);
      return get(`/v1/hooks?${params}`);
    },
  },

  usage: {
    all: (days = 30): Promise<{
      teamId: string; days: number;
      agents: Array<{ agentId: string; agentName: string; agentEmoji: string; totalTokensIn: number; totalTokensOut: number; totalCostUsd: number }>;
    }> => get(`/v1/usage?days=${days}`),

    byAgent: (agentId: string, days = 30): Promise<{
      agentId: string; teamId: string; days: number;
      usage: Array<{ agentId: string; date: string; tokensIn: number; tokensOut: number; costUsd: number; source: string }>;
    }> => get(`/v1/agents/${agentId}/usage?days=${days}`),

    /** Team-wide usage overview with daily breakdown and plan info */
    overview: (days = 30): Promise<{
      agents: Array<{ agentId: string; totalTokensIn: number; totalTokensOut: number; totalCostUsd: number }>;
      daily: Array<{ date: string; tokensIn: number; tokensOut: number; costUsd: number }>;
      plan: { plan: string; creditsRemaining: number; creditsMonthly: number; trialExpired: boolean };
    }> => get(`/v1/agents/usage?days=${days}`),
  },

  feedback: {
    submit: (agentId: string, opts: {
      reaction: 'positive' | 'negative' | 'neutral';
      comment?: string;
      messageId?: string;
      deliverableId?: string;
      taskId?: string;
      contextType?: 'chat' | 'task' | 'content' | 'image' | 'document';
      contextContent?: string;
    }): Promise<{ feedback: { id: string; reaction: string } }> =>
      post(`/v1/agents/${agentId}/feedback`, opts),

    list: (agentId: string, limit = 50): Promise<{
      agentId: string;
      feedback: Array<{
        id: string; reaction: string; comment?: string; contextType: string;
        contextContent?: string; createdAt: string;
      }>;
    }> => get(`/v1/agents/${agentId}/feedback?limit=${limit}`),

    rules: (agentId: string): Promise<{
      agentId: string;
      rules: Array<{ id: string; tier: string; rule: string; sourceCount: number }>;
    }> => get(`/v1/agents/${agentId}/feedback/rules`),
  },

  team: {
    /** Everyone on the caller's team — for "share with these members" pickers. */
    members: (): Promise<{ teamId: string; members: TeamPerson[]; me: string }> => get('/v1/team/members'),
  },

  dashboards: {
    /** Catalogue of data sources a dashboard can use for this brand, with live status and ideas to play with. */
    sources: (brandId?: string): Promise<{ groups: DashboardSourceGroup[]; checkedAt: string }> =>
      get(`/v1/dashboards/sources${brandId ? `?brandId=${brandId}` : ''}`),
    list: (brandId?: string): Promise<{ dashboards: Dashboard[] }> =>
      get(`/v1/dashboards${brandId ? `?brandId=${brandId}` : ''}`),
    get: (id: string): Promise<Dashboard> =>
      get(`/v1/dashboards/${id}`),
    create: (data: {
      title: string; description?: string; type?: string;
      dataConfig?: string; linkedOkrId?: string; autoRefresh?: boolean;
      brandId?: string;
    }): Promise<Dashboard> =>
      post('/v1/dashboards', data),
    /** Take ownership of a dashboard that has none. */
    claim: (id: string): Promise<{ id: string; ownerId: string; isOwner: boolean; unowned: boolean }> => post(`/v1/dashboards/${encodeURIComponent(id)}/claim`, {}),
    update: (id: string, data: Partial<Dashboard> & { delivery?: DashboardDelivery | null; allowedUserIds?: string[] }): Promise<Dashboard> =>
      patch(`/v1/dashboards/${id}`, data),
    delete: (id: string): Promise<{ deleted: boolean }> =>
      del(`/v1/dashboards/${id}`),
    duplicate: (id: string, title?: string): Promise<Dashboard> =>
      post(`/v1/dashboards/${id}/duplicate`, title ? { title } : {}),
    /**
     * Data-only refresh (re-run sources, re-inject into the stored template;
     * seconds, no AI) unless `redesign` is set or no template exists yet — then
     * a full AI render runs in the background and the response is 202 with
     * renderStatus 'rendering'; poll `get` until it flips to 'ready'/'failed'.
     */
    refresh: (id: string, opts: { redesign?: boolean } = {}): Promise<{ renderStatus: 'ready' | 'rendering' | 'failed'; mode: 'full' | 'data'; datasets?: Array<{ label: string; count: number; error?: string }> }> =>
      post(`/v1/dashboards/${id}/refresh`, opts),
    /** v2 (spec) dashboards — native renderer. */
    templates: (): Promise<{ templates: Array<{ id: string; name: string; cluster: string; description: string; brief: { kind: 'campaign' | 'brand' | 'text'; label: string; placeholder: string }; suggestions: string[]; widgets: Array<{ type: string; w: number; title?: string }> }> }> =>
      get('/v1/dashboards/templates'),
    /** Run the template's sources and widgets against live data before creating: what would be empty, and why. */
    preflight: (body: { templateId: string; campaignId?: string; campaignName?: string; brandId?: string; brandName?: string; period?: string }): Promise<DashboardPreflight> =>
      post('/v1/dashboards/preflight', body),
    generate: (body: { templateId: string; brief?: string; campaignId?: string; campaignName?: string; brandId?: string; brandName?: string; period?: string; title?: string; force?: boolean; visibility?: Visibility; allowedUserIds?: string[] }): Promise<Dashboard & { hasSpec?: boolean; templateId?: string | null }> =>
      post('/v1/dashboards/generate', body),
    edit: (id: string, instruction: string): Promise<{ spec: any; data: any; note: string; changed: string[] }> =>
      post(`/v1/dashboards/${id}/edit`, { instruction }),
    rebuild: (id: string): Promise<{ renderStatus: string }> => post(`/v1/dashboards/${id}/rebuild`, {}),
    rebuildAll: (brandId?: string): Promise<{ queued: number; ids: string[] }> => post(`/v1/dashboards/rebuild-all${brandId ? `?brandId=${brandId}` : ''}`, {}),
    exportDeck: (id: string): Promise<{ presentationId: string; title: string; pages: number }> =>
      post(`/v1/dashboards/${id}/export-deck`, {}),
    callSession: (id: string): Promise<{ clientSecret: string; model: string; instructions: string; voice: string; userName: string | null }> =>
      post(`/v1/dashboards/${id}/call-session`, {}),
    saveSpec: (id: string, spec: any): Promise<{ ok: boolean }> =>
      patch(`/v1/dashboards/${id}/spec`, { spec }),
    embedUrl: (id: string, shareToken: string) =>
      `${API_BASE_URL}/v1/dashboards/${id}/embed?token=${shareToken}`,
    /** Owner only: turn "anyone with the link can view" on/off; rotate mints a fresh link. */
    share: (id: string, data: { enabled: boolean; rotate?: boolean }): Promise<{ id: string; shareEnabled: boolean; shareToken: string }> =>
      post(`/v1/dashboards/${id}/share`, data),
    /** The public viewer URL (served by this app, no login). */
    publicUrl: (id: string, shareToken: string) => `${window.location.origin}/share/dashboard/${id}?t=${shareToken}`,
    /** Unauthenticated fetch for the public viewer. */
    public: async (id: string, shareToken: string): Promise<PublicDashboard> => {
      const r = await fetch(`${API_BASE_URL}/v1/dashboards/${id}/public?token=${encodeURIComponent(shareToken)}`);
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error((j as { error?: string }).error || `HTTP ${r.status}`); }
      return r.json();
    },
    /**
     * Build (or fetch from cache) an HTML presentation for these dashboards.
     * Returns a presentationId — open <PresentationModal> with it to view, then
     * export to PDF or PPTX from inside the modal.
     */
    export: (dashboardIds: string[]): Promise<{ presentationId: string; title: string }> =>
      post('/v1/dashboards/export', { dashboardIds }),
  },

  wiki: {
    list: (brandId = 'default'): Promise<{
      pages: Array<{
        slug: string; category: string; title: string;
        parentSlug: string | null; summary: string;
        sourceCount: number; updatedAt: string;
      }>;
    }> => get(`/v1/wiki?brandId=${brandId}`),
    get: (slug: string, brandId = 'default'): Promise<{
      slug: string; category: string; title: string;
      bodyMd: string; renderedMd: string; parentSlug: string | null;
      sources: Array<{ id: string; type: string; title: string; url: string | null; ingestedAt: string }>;
      createdAt: string; updatedAt: string;
    }> => get(`/v1/wiki/pages/${encodeURIComponent(slug)}?brandId=${brandId}`),
    log: (brandId = 'default', limit = 100): Promise<{
      entries: Array<{ id: string; event: string; message: string; details: Record<string, unknown>; createdAt: string }>;
    }> => get(`/v1/wiki/log?brandId=${brandId}&limit=${limit}`),
    sources: (brandId = 'default', limit = 100): Promise<{
      sources: Array<{ id: string; teamId: string; brandId: string; type: string; title: string; url: string | null; contentMd: string | null; pagesTouched: string[]; ingestedAt: string }>;
    }> => get(`/v1/wiki/sources?brandId=${brandId}&limit=${limit}`),
    /** One score per brand: coverage of the key Client Brain pages, provenance, freshness. */
    quality: (brandId = 'default'): Promise<WikiQuality> => get(`/v1/wiki/quality?brandId=${brandId}`),
    /** The work writes the wiki: results + gated learnings on campaign pages, first drafts of the stubs, Jev contradiction lint. Background job. */
    enrich: (input: { brandId: string; campaigns?: boolean; stubs?: boolean; lint?: boolean; force?: boolean }): Promise<{ started: boolean; running?: boolean }> => post('/v1/wiki/enrich', input),
    enrichStatus: (brandId: string): Promise<WikiEnrichStatus> => get(`/v1/wiki/enrich/status?brandId=${brandId}`),
    ingest: (input: {
      type: 'url' | 'pdf' | 'transcript' | 'chat_session' | 'slack' | 'note';
      title: string;
      url?: string;
      contentMd?: string;
      brandId?: string;
    }): Promise<{
      sourceId: string;
      created: string[];
      updated: string[];
      summary: string;
    }> => post('/v1/wiki/ingest', input),
    save: (slug: string, body: { bodyMd?: string; title?: string; category?: string; brandId?: string }): Promise<{
      slug: string; title: string; bodyMd: string; updatedAt: string;
    }> => patch(`/v1/wiki/pages/${encodeURIComponent(slug)}`, body),
    create: (input: { category: string; title: string; bodyMd?: string; slug?: string; brandId?: string }): Promise<{
      slug: string; title: string; category: string; bodyMd: string;
    }> => post('/v1/wiki/pages', input),
    delete: (slug: string, brandId = 'default'): Promise<{ deleted: boolean; slug: string }> =>
      del(`/v1/wiki/pages/${encodeURIComponent(slug)}?brandId=${brandId}`),
    lint: (brandId = 'default', status: 'open' | 'all' = 'open'): Promise<{
      findings: Array<{
        id: string; type: 'stale' | 'orphan' | 'missing_link' | 'contradiction';
        pageSlug: string | null; description: string;
        severity: 'info' | 'warn' | 'error';
        status: 'open' | 'resolved' | 'dismissed';
        detectedAt: string; resolvedAt: string | null;
      }>;
    }> => get(`/v1/wiki/lint?brandId=${brandId}&status=${status}`),
    runLint: (input: { brandId?: string; skipContradictions?: boolean } = {}): Promise<{
      stale: number; orphan: number; missingLink: number; contradiction: number; total: number;
    }> => post('/v1/wiki/lint/run', input),
    setLintStatus: (id: string, status: 'open' | 'resolved' | 'dismissed', brandId = 'default'): Promise<{
      id: string; status: string;
    }> => patch(`/v1/wiki/lint/${encodeURIComponent(id)}`, { status, brandId }),
  },

  projects: {
    list: (opts?: { brandId?: string; pinnedOnly?: boolean }): Promise<{ projects: Project[] }> => {
      const p = new URLSearchParams();
      if (opts?.brandId) p.set('brandId', opts.brandId);
      if (opts?.pinnedOnly) p.set('pinned', 'true');
      const qs = p.toString();
      return get(`/v1/projects${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<{
      project: Project;
      tasks: Array<Record<string, unknown>>;
      okrs: Array<Record<string, unknown>>;
      dashboards: Array<Record<string, unknown>>;
    }> => get(`/v1/projects/${id}`),
    listTemplates: (): Promise<{ templates: ProjectTemplate[] }> =>
      get('/v1/projects/templates'),
    create: (data: {
      title: string; description?: string; emoji?: string;
      brandId?: string; template?: string; assignedAgentIds?: string[];
    }): Promise<{ project: Project; instantiated: { tasks: number; okrs: number } }> =>
      post('/v1/projects', data),
    update: (id: string, data: Partial<{
      title: string; description: string; emoji: string; status: string;
      assignedAgentIds: string[];
    }>): Promise<{ project: Project }> =>
      patch(`/v1/projects/${id}`, data),
    pin: (id: string, pinned: boolean): Promise<{ project: Project }> =>
      post(`/v1/projects/${id}/pin`, { pinned }),
    delete: (id: string): Promise<{ deleted: boolean }> =>
      del(`/v1/projects/${id}`),
  },

  /** Bundles: several proposals and/or media plans for one client as one named source for any report template. */
  bundles: {
    list: (brandId?: string): Promise<{ bundles: Bundle[] }> => get(`/v1/bundles${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    sources: (brandId?: string): Promise<BundleSources> => get(`/v1/bundles/sources${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    create: (data: { name: string; brandId?: string | null; items: BundleItem[] }): Promise<{ bundle: Bundle }> => post('/v1/bundles', data),
    update: (id: string, data: { name?: string; items?: BundleItem[] }): Promise<{ bundle: Bundle }> => patch(`/v1/bundles/${id}`, data),
    remove: (id: string): Promise<{ ok: boolean }> => del(`/v1/bundles/${id}`),
    programmeUrl: (id: string, doc: 'onepager' | 'executive', as: 'html' | 'pptx' = 'html') => `${API_BASE_URL}/v1/bundles/${id}/programme?doc=${doc}&as=${as}`,
  },
  mediaPlans: {
    list: (brandId?: string): Promise<{ plans: MediaPlan[] }> => get(`/v1/media-plans${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    get: (id: string): Promise<{ plan: MediaPlan; sharing?: SharingInfo; kickoffFields: Record<string, string | string[]>; proposalChannels: ProposalChannelFromPlan[]; audience?: { union: PlanAudienceSize['union']; fullAudience: boolean; at: string; reach: PlanReach; reachTarget: PlanReachTarget | null } | null; brief: Record<string, string>; preset: string | null; presetCreative: string | null }> => get(`/v1/media-plans/${id}`),
    create: (data: Partial<Omit<MediaPlan, 'totals'>>): Promise<{ plan: MediaPlan }> => post('/v1/media-plans', data),
    /** Take ownership of a plan that has none. */
    claim: (id: string): Promise<{ plan: MediaPlan; sharing?: SharingInfo }> => post(`/v1/media-plans/${encodeURIComponent(id)}/claim`, {}),
    update: (id: string, data: Partial<Omit<MediaPlan, 'totals'>> & { visibility?: SharingVisibility; sharedWith?: string[] }): Promise<{ plan: MediaPlan; rebuilt?: boolean }> => patch(`/v1/media-plans/${id}`, data),
    remove: (id: string): Promise<{ ok: boolean }> => del(`/v1/media-plans/${id}`),
    duplicate: (id: string): Promise<{ plan: MediaPlan }> => post(`/v1/media-plans/${id}/duplicate`, {}),
    benchmarks: (brandId?: string): Promise<MediaPlanBenchmarks> => get(`/v1/media-plans/benchmarks${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    audienceSize: (id: string): Promise<PlanAudienceSize> => get(`/v1/media-plans/${id}/audience-size`),
    reachTarget: (id: string, data: { targetPct?: number; save?: boolean; clear?: boolean }): Promise<{ plan: MediaPlan | null; reachTarget: PlanReachTarget | null; solved?: { budget: number; multiplier: number; reach: PlanReach }; current?: PlanReach }> => post(`/v1/media-plans/${id}/reach-target`, data),
    recommend: (data: { planId?: string; totalBudget: number; currency: string; market?: string; products: Array<'creator' | 'ugc' | 'boost' | 'ampli'>; takeRatePct?: number; band?: string; objective?: string; dateDrafts?: string; dateDeliver?: string }): Promise<{ beats: MediaPlanBeat[]; takeRatePct: number; why: string[] }> => post('/v1/media-plans/recommend', data),
    presets: (): Promise<{ presets: MediaPlanPreset[] }> => get('/v1/media-plans/presets'),
    applyPreset: (id: string, presetId: string, products?: Array<'creator' | 'ugc' | 'boost' | 'ampli'>): Promise<{ plan: MediaPlan; why: string[]; preset: { id: string; name: string; questions: string[] } }> => post(`/v1/media-plans/${id}/apply-preset`, { presetId, products }),
    setBoostObjective: (id: string, objective: string): Promise<{ plan: MediaPlan; objective: string; label: string }> => post(`/v1/media-plans/${id}/boost-objective`, { objective }),
    fromPast: (campaign: PastCampaign, band?: string): Promise<{ beats: MediaPlanBeat[] }> => post('/v1/media-plans/from-past', { campaign, band }),
    toKickoff: (id: string, kickoffId?: string): Promise<{ kickoffId: string; fields: string[] }> => post(`/v1/media-plans/${id}/to-kickoff`, { kickoffId }),
    priceBook: (): Promise<PriceBook> => get('/v1/media-plans/price-book'),
    /** 2026/27 pricing: the catalogue, the client's terms (rate card, subscription), a programme's economics, bulk changes, history. */
    pricing: {
      catalogue: (): Promise<{ catalogue: PricingCatalogue; rateCardKeys: string[] }> => get('/v1/media-plans/pricing/catalogue'),
      terms: (brandId?: string | null): Promise<{ terms: ClientPricingTerms }> => get(`/v1/media-plans/pricing/terms${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
      setTerms: (brandId: string | null | undefined, patch: Partial<ClientPricingTerms>): Promise<{ terms: ClientPricingTerms }> => put(`/v1/media-plans/pricing/terms${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`, patch),
      programme: (ids: string[], opts: { months?: number; tracking?: boolean } = {}): Promise<{ programme: ProgrammeEconomics; terms: ClientPricingTerms; legacy: string[] }> => get(`/v1/media-plans/pricing/programme?ids=${ids.map(encodeURIComponent).join(',')}${opts.months != null ? `&months=${opts.months}` : ''}${opts.tracking != null ? `&tracking=${opts.tracking ? 1 : 0}` : ''}`),
      /** `fit` closes the gap a move leaves: raise the client total, or take it from paid media (creators keep their pay). */
      bulk: (body: { ids: string[]; apply2026?: boolean; commercial?: Partial<CommercialTerms>; creatorTakePct?: number | null; rateCard?: Record<string, number>; fit?: 'raise_total' | 'reduce_paid'; preview?: boolean }): Promise<{ preview: boolean; plans: Array<{ id: string; name: string; before: PlanEconomics | null; legacyBefore: { revenue: number; takeRatePct: number | null } | null; after: PlanEconomics }> }> => post('/v1/media-plans/pricing/bulk', body),
      history: (id: string): Promise<{ history: Array<{ at: string; by: string | null; note: string; totalBudget: number; beats: number; pricing: '2026/27' | 'legacy' }> }> => get(`/v1/media-plans/${encodeURIComponent(id)}/history`),
      restore: (id: string, at: string): Promise<{ plan: MediaPlan }> => post(`/v1/media-plans/${encodeURIComponent(id)}/restore`, { at }),
      /** A deck's budget table from the plans (client view): one row per plan, the platform row and the total; the slide keeps `source` for refresh. */
      slide: (ids: string[], opts: { rounding?: 'exact' | '50k' | '100k'; lang?: 'sv' | 'en'; months?: number } = {}): Promise<{ slide: Record<string, unknown> & { source: PlansTableSource }; updatedAt: string | null }> => get(`/v1/media-plans/pricing/slide?ids=${ids.map(encodeURIComponent).join(',')}${opts.rounding ? `&rounding=${opts.rounding}` : ''}${opts.lang ? `&lang=${opts.lang}` : ''}${opts.months != null ? `&months=${opts.months}` : ''}`),
      exportXlsx: async (ids: string[], view: 'client' | 'internal', opts: { months?: number; name?: string } = {}): Promise<Blob> => {
        const r = await fetch(`${BASE_URL}/v1/media-plans/pricing/export?ids=${ids.map(encodeURIComponent).join(',')}&view=${view}${opts.months != null ? `&months=${opts.months}` : ''}${opts.name ? `&name=${encodeURIComponent(opts.name)}` : ''}`, { headers: authHeaders(false) });
        if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `Export failed (${r.status})`);
        return r.blob();
      },
    },
    deals: (year = '2026'): Promise<DealBenchmarks> => get(`/v1/media-plans/deals?year=${year}`),
    agreementDraft: (id: string, data: { company: string; orgNumber?: string; countryCode?: string; participants: Array<{ name: string; email: string; signatory?: boolean }>; createdByEmail?: string; contractName?: string }): Promise<{ id: string; url: string; name: string; attachment: boolean; inlinePdf: boolean; dataFieldsFilled: string[]; counterparty: boolean; createdAs: string }> => post(`/v1/media-plans/${id}/agreement-draft`, data),
    documentUrl: (id: string) => `${API_BASE_URL}/v1/media-plans/${id}/document`,
    /** ONE Oneflow agreement for several plans (same client, same currency); every plan records it. */
    bundleAgreementDraft: (data: { ids: string[]; company: string; orgNumber?: string; countryCode?: string; participants: Array<{ name: string; email: string; signatory?: boolean }>; createdByEmail?: string; contractName?: string; bundleName?: string; replaceExisting?: boolean }): Promise<{ id: string; url: string; name: string; attachment: boolean; inlinePdf: boolean; dataFieldsFilled: string[]; counterparty: boolean; createdAs: string; planIds: string[]; warning?: string }> => post('/v1/media-plans/bundle/agreement-draft', data),
    /** The client-facing document that bundle agreement carries (summary + every plan), as HTML. */
    bundleDocumentUrl: (ids: string[], company?: string, name?: string) => `${API_BASE_URL}/v1/media-plans/bundle/document?ids=${ids.map(encodeURIComponent).join(',')}${company ? `&company=${encodeURIComponent(company)}` : ''}${name ? `&name=${encodeURIComponent(name)}` : ''}`,
    /** Several plans as one document: the A4 programme one-pager, or the executive two-pager (calendar + KPI table) as HTML or .pptx. */
    programmeUrl: (ids: string[], doc: 'onepager' | 'executive', as: 'html' | 'pptx' = 'html') => `${API_BASE_URL}/v1/media-plans/programme?ids=${ids.map(encodeURIComponent).join(',')}&doc=${doc}&as=${as}`,
    chat: (id: string, body: { message: string; history: Array<{ role: 'vera' | 'user'; text: string }> }): Promise<MediaPlanChatResult> => post(`/v1/media-plans/${id}/chat`, body),
    realtimeSecret: (id: string): Promise<{ clientSecret: string; expiresAt: number; model: string; instructions: string; voice: string; userName: string | null }> => post(`/v1/media-plans/${id}/realtime-secret`, {}),
    consult: (id: string, body: { question: string; context?: string }): Promise<{ answer: string; brief?: Record<string, string> }> => post(`/v1/media-plans/${id}/consult`, body),
    fromDeal: (dealId: string): Promise<{ header: { name: string; clientName: string | null; market: string | null; currency: string; totalBudget: number; takeRatePct: number | null }; beats: MediaPlanBeat[]; why: string[] }> => post('/v1/media-plans/from-deal', { dealId }),
  },

  presentations: {
    /** Pre-publish checks on every page (banned wording, promised results, unfinished template text, unsourced statistics). `available: false` when the checker is not configured on the server. */
    verification: (id: string): Promise<DeckVerification> => get(`/v1/presentations/${encodeURIComponent(id)}/verification`),
    guardrails: (id: string, opts: { facts?: boolean } = {}): Promise<GuardrailReport> => get(`/v1/presentations/${encodeURIComponent(id)}/guardrails${opts.facts === false ? '?facts=0' : ''}`),
    /** One spoken instruction → a typed deck command (Jev, ~300 ms); `uncertain` = ask before acting. */
    voiceCommand: (id: string, body: { utterance: string; index: number; pages: Array<{ type?: string; title?: string }> }): Promise<{ command: DeckCommand; uncertain: boolean }> => post(`/v1/presentations/${encodeURIComponent(id)}/voice-command`, body),
    /** Start from scratch: Lykke reads uploaded material (PDF, PowerPoint, Word, text, pictures) and writes the deck brief. Nothing is created. */
    briefFromMaterial: async (files: File[], opts: { text?: string; brandId?: string } = {}): Promise<{ title: string; brief: string; pages: number; sources: string[]; notes: string[] }> => {
      const fd = new FormData(); for (const f of files) fd.append('files', f, f.name);
      if (opts.text) fd.append('text', opts.text); if (opts.brandId) fd.append('brandId', opts.brandId);
      const res = await fetch(`${API_BASE_URL}/v1/presentations/start/brief-from-material`, { method: 'POST', headers: authHeaders(false), body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((j as { error?: string }).error || `Could not read the material (${res.status})`);
      return j as { title: string; brief: string; pages: number; sources: string[]; notes: string[] };
    },
    /** Start from scratch: a short-lived key for a strategy call with Lykke (the browser holds update_brief / start_building). */
    strategizeSecret: (body: { brandId?: string; language?: string; brief?: string }): Promise<{ clientSecret: string; expiresAt: number; model: string; instructions: string; voice: string; userName: string }> =>
      post('/v1/presentations/start/strategize-secret', body),
    /** Images / videos from the user's computer for slides; videos come back with a poster frame when the server could make one. */
    uploadMedia: async (files: File[]): Promise<{ files: Array<{ url: string; posterUrl?: string; kind: 'image' | 'video'; name: string; size: number }> }> => {
      const fd = new FormData(); for (const f of files) fd.append('files', f, f.name);
      const res = await fetch(`${API_BASE_URL}/v1/presentations/upload-media`, { method: 'POST', headers: authHeaders(false), body: fd });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Upload failed (${res.status})`); }
      return res.json();
    },
    /** Beatly's creator database (the caller's own login): handle prefix matches, then names. */
    searchCreators: (q: string): Promise<{ creators: Array<{ id: string; handle: string; platform: string; name: string; avatarUrl: string | null; followers: number | null; engagementRate: number | null; verified: boolean; country: string | null }> }> =>
      get(`/v1/presentations/creator-search?q=${encodeURIComponent(q)}`),
    /** Lykke presents this deck live: short-lived Realtime client secret with the deck script in the instructions. */
    presentSession: (id: string, opts?: { mode?: 'present' | 'copilot' | 'build'; slides?: Array<Record<string, unknown>>; language?: string; voice?: string }): Promise<{ mode?: 'present' | 'copilot' | 'build'; clientSecret: string; expiresAt?: number; model: string; instructions: string; voice: string; userName: string | null; pages: number; knowledgeChars?: number; budget?: { maxChars: number; instructionsChars: number; scriptChars: number; knowledgeChars: number; knowledgeBudget: number; scriptTrimmed: boolean; knowledgeTrimmed: boolean; approxTokens: number }; warning?: string | null }> =>
      post(`/v1/presentations/${id}/present-session`, opts || {}),
    /** GPT-Live (experimental engine for "Lykke presents"): the backend exchanges the browser's WebRTC offer with our key. */
    liveSession: (id: string, body: { sdp: string; voice?: string }): Promise<{ sdp: string; sessionId: string | null; model: string; voice: string; pages: string[]; userName: string | null; title: string }> =>
      post(`/v1/presentations/${id}/live-session`, body),
    /** The facts that answer what the listener is saying (brand facts layer + deck, matched by Jev): sent to her as quiet context. */
    liveContext: (id: string, body: { utterance: string; recent?: string; page: number }): Promise<{ facts: Array<{ label: string; text: string; p: number }>; ms: number; jevMs?: number; layerAt?: string | null }> =>
      post(`/v1/presentations/${id}/live-context`, body),
    /** What the listener's words mean for the pages (GPT-Live cannot move them): a page to show, or nothing. */
    liveNav: (id: string, body: { utterance: string; recent: Array<{ role: 'user' | 'lykke'; text: string }>; page: number }): Promise<{ action: 'goto' | 'none'; page?: number }> =>
      post(`/v1/presentations/${id}/live-nav`, body),
    /** A question GPT-Live delegated: 1–3 spoken sentences from the deck and the brand knowledge. */
    liveAnswer: (id: string, body: { transcript: Array<{ role: 'user' | 'lykke'; text: string }>; page: number }): Promise<{ answer: string }> =>
      post(`/v1/presentations/${id}/live-answer`, body),
    /** Beatly people for a "Your team" page — employee directory for staff logins, the team's account manager for clients. */
    teamMembers: (): Promise<{ members: TeamMember[]; mode?: 'employees' | 'client' }> => get('/v1/presentations/team-members'),
    /** Owner only: "anyone with the link can view" on/off; rotate mints a fresh link. */
    share: (id: string, data: { enabled: boolean; rotate?: boolean }): Promise<{ id: string; shareEnabled: boolean; shareToken: string }> =>
      post(`/v1/presentations/${id}/share`, data),
    publicUrl: (id: string, shareToken: string) => `${window.location.origin}/share/report/${id}?t=${shareToken}`,
    /** Unauthenticated fetch for the public viewer. */
    public: async (id: string, shareToken: string): Promise<PublicPresentation> => {
      const r = await fetch(`${API_BASE_URL}/v1/presentations/${id}/public?token=${encodeURIComponent(shareToken)}`);
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error((j as { error?: string }).error || `HTTP ${r.status}`); }
      return r.json();
    },
    /** Everything the creator API knows about one creator, shaped for a slide card (no contact details). */
    creatorFacts: (q: { id?: string; handle?: string; platform?: string }): Promise<CreatorFacts> => {
      const p = new URLSearchParams(); if (q.id) p.set('id', q.id); if (q.handle) p.set('handle', q.handle); if (q.platform) p.set('platform', q.platform);
      return get(`/v1/presentations/creator-facts?${p.toString()}`);
    },
    /** Creator profile picture by handle (Instagram / TikTok, public profile) — copied into the team's media so the slide never depends on a CDN URL. */
    lookupCreator: (body: { handle: string; platform: 'INSTAGRAM' | 'TIKTOK'; imageUrl?: string; name?: string }): Promise<{ url: string; handle: string; platform: 'INSTAGRAM' | 'TIKTOK'; name: string; followers?: number; bio?: string; verified?: boolean; profileUrl: string; width?: number; quality?: 'hd' | 'thumbnail' }> =>
      post('/v1/presentations/creator-avatar', body),
    /** List presentations. With { sessionId } filters to those generated in that chat session. */
    list: (opts?: { sessionId?: string }): Promise<{
      presentations: Array<{
        id: string; title: string; sourceType: string; sourceId: string | null; brandId?: string;
        slideCount?: number; cover?: Record<string, unknown> | null;
        templateId?: string | null; templateName?: string | null;
        generation?: { status: 'running' | 'done' | 'failed'; error?: string; startedAt?: string; kind?: 'proposal' | 'kai' | 'freestyle'; step?: number; stepsTotal?: number; stepLabel?: string } | null;
        brand?: Record<string, unknown>;
        visibility?: Visibility; allowedUserIds?: string[]; isOwner?: boolean; unowned?: boolean;
        shareEnabled?: boolean; shareToken?: string | null;
        createdAt: string; updatedAt: string;
      }>;
    }> => {
      const qs = opts?.sessionId ? `?sessionId=${encodeURIComponent(opts.sessionId)}` : '';
      return get(`/v1/presentations${qs}`);
    },

    get: (id: string): Promise<{
      id: string;
      title: string;
      sourceType?: string;
      brandId?: string;
      settings?: DeckSettings;
      shareEnabled?: boolean; shareToken?: string | null;
      slides: Array<Record<string, unknown>>;
      brand: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }> => get(`/v1/presentations/${id}`),

    /** Create an editable report deck ("Create a report"). */
    create: (data: { visibility?: Visibility; allowedUserIds?: string[]; title?: string; brandId?: string; slides?: Array<Record<string, unknown>>; templateId?: string; templateName?: string; pendingBrief?: string; pendingMode?: 'deck' | 'page'; pendingAutoBuild?: boolean; /** The person picked the brand; a brand named in the brief does not override it. */ brandChosen?: boolean } = {}): Promise<{ id: string; title: string; slideCount: number }> =>
      post('/v1/presentations', data),
    /**
     * "Text to slides": create a deck from a brief. Responds at once with the
     * new deck (cover only, generation running); Kai builds the pages in the
     * background — poll `get` until settings.generation.status is done/failed.
     */
    generate: (data: { visibility?: Visibility; allowedUserIds?: string[]; title?: string; brandId?: string; brief: string; pages?: number; campaign?: { id?: string; name: string }; templateName?: string; templateId?: string; outline?: Array<Record<string, unknown>>; fetchComments?: boolean; flexiblePages?: boolean; aiContent?: AiContentOptions; /** A bundle of proposals / media plans as the source (required by the programme templates, optional elsewhere). */ bundleId?: string; brandChosen?: boolean }): Promise<{ id: string; title: string; slideCount: number; generation?: DeckSettings['generation'] }> =>
      post('/v1/presentations/generate', data),
    /** Generate more AI concept visuals for an existing deck (appended to settings.generation.aiContent.items). */
    aiContent: (id: string, data: Partial<AiContentOptions> & { brief?: string } = {}): Promise<{ status: string; items: AiContentItem[]; all: AiContentItem[]; errors: string[] }> =>
      post(`/v1/presentations/${id}/ai-content`, data),
    /** Re-run text-to-slides on an existing deck with its stored brief/campaign/pages (or overrides). */
    regenerate: (id: string, data: { brief?: string; pages?: number; campaign?: { id?: string; name: string }; fetchComments?: boolean; flexiblePages?: boolean; aiContent?: AiContentOptions } = {}): Promise<{ id: string; generation?: DeckSettings['generation'] }> =>
      post(`/v1/presentations/${id}/regenerate`, data),
    /** Save edits — title and/or the full slides array. */
    /** Take ownership of a report that has none (made before ownership existed). */
    claim: (id: string): Promise<{ id: string; ownerId: string; isOwner: boolean; unowned: boolean }> => post(`/v1/presentations/${encodeURIComponent(id)}/claim`, {}),
    update: (id: string, data: { visibility?: Visibility; allowedUserIds?: string[]; title?: string; slides?: Array<Record<string, unknown>>; settings?: DeckSettings | null; /** Move the deck to another brand of the same team. */ brandId?: string }): Promise<{ id: string; title: string; slideCount: number; updatedAt: string } & Partial<VisibilityInfo>> =>
      patch(`/v1/presentations/${id}`, data),
    delete: (id: string): Promise<{ deleted: boolean }> => del(`/v1/presentations/${id}`),
    duplicate: (id: string, title?: string): Promise<{ id: string; title: string }> =>
      post(`/v1/presentations/${id}/duplicate`, title ? { title } : {}),
    /** Tell Lykke: one typed message from any page → actions on the deck (move, delete, duplicate, add, go to, revise) and a short reply. */
    tell: (id: string, body: { instruction: string; index: number; outline: Array<{ title?: string; type?: string; planned?: boolean }> }): Promise<{ reply: string; ops: TellOp[] }> =>
      post(`/v1/presentations/${encodeURIComponent(id)}/tell`, body),
    /** Beta (staff): a copy where Claude composes every page freely inside the design contract. Pages land as they are written. */
    freestyle: (id: string): Promise<{ id: string; title: string }> => post(`/v1/presentations/${encodeURIComponent(id)}/freestyle`, {}),
    /**
     * Kai builds 1–3 slides from a one-line brief using live datalake rows and
     * Content Hub media. Returns the slides only — the editor saves via `update`.
     */
    generateSlides: (id: string, data: { prompt: string; index?: number; maxSlides?: number; slides?: Array<Record<string, unknown>>; quick?: boolean }): Promise<{ slides: Array<Record<string, unknown>>; toolCalls: number; note?: string; otherWorkspace?: boolean }> =>
      post(`/v1/presentations/${id}/slides/generate`, data),
    /** Same as generateSlides, with progress labels while Kai reads and writes ("Reading paid ad rows", "Writing the page"). */
    generateSlidesStream: async (id: string, data: { prompt: string; index?: number; maxSlides?: number; slides?: Array<Record<string, unknown>>; quick?: boolean }, onProgress: (label: string) => void): Promise<{ slides: Array<Record<string, unknown>>; toolCalls: number; note?: string; otherWorkspace?: boolean }> => {
      const res = await fetch(`${API_BASE_URL}/v1/presentations/${id}/slides/generate/stream`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) });
      if (!res.ok) { const err = await res.json().catch(() => ({ error: res.statusText })); throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`); }
      const reader = res.body!.getReader(); const decoder = new TextDecoder(); let buffer = ''; let result: any = null; let error: string | null = null;
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n'); buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
          try { const ev = JSON.parse(line.slice(6)); if (ev.event === 'progress') onProgress(String(ev.label || '')); else if (ev.event === 'done') result = ev; else if (ev.event === 'error') error = String(ev.error || 'failed'); } catch { /* ignore */ }
        }
      }
      if (error) throw new Error(error);
      if (!result) throw new Error('The connection dropped before the page was written');
      return { slides: result.slides || [], toolCalls: result.toolCalls || 0, note: result.note, otherWorkspace: result.otherWorkspace };
    },
    /**
     * One instruction for the deck: Claude decides the scope (the whole deck, some pages, the page on screen) and a
     * brand switch, then rewrites the pages in parallel. Events stream in: plan → working/page per page → done.
     */
    /** Deck ideas from the brand's own campaigns (what Deck studio suggests). */
    ideas: (brandId?: string): Promise<{ ideas: string[] }> => get(`/v1/presentations/ideas${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    /** The deck's chat with Lykke (last 40 turns). */
    chatHistory: (id: string): Promise<{ turns: Array<{ role: 'user' | 'assistant'; text: string; at: string; decisions?: Array<{ question: string; options: string[] }> }>; working?: boolean }> => get(`/v1/presentations/${encodeURIComponent(id)}/chat`),
    /** Stop Lykke's running message on this deck (on whichever server runs it). */
    chatStop: (id: string): Promise<{ ok: boolean }> => post(`/v1/presentations/${encodeURIComponent(id)}/chat/stop`, {}),
    /** One message to Lykke: she acts on the deck; events stream in (status, text, page, deck, go_to, present, done). */
    chat: async (id: string, data: { message: string; index: number }, onEvent: (ev: DeckChatEvent) => void, signal?: AbortSignal): Promise<void> => {
      const res = await fetch(`${API_BASE_URL}/v1/presentations/${encodeURIComponent(id)}/chat`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data), signal });
      if (!res.ok) { const err = await res.json().catch(() => ({ error: res.statusText })); throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`); }
      const reader = res.body!.getReader(); const decoder = new TextDecoder(); let buffer = ''; let failed: string | null = null;
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n'); buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
          try { const ev = JSON.parse(line.slice(6)) as DeckChatEvent; if (ev.event === 'error') failed = String(ev.error || 'failed'); else onEvent(ev); } catch { /* partial line */ }
        }
      }
      if (failed) throw new Error(failed);
    },
    revise: async (id: string, data: { instruction: string; index: number; slides: Array<Record<string, unknown>> }, onEvent: (ev: ReviseEvent) => void): Promise<void> => {
      const res = await fetch(`${API_BASE_URL}/v1/presentations/${encodeURIComponent(id)}/revise`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) });
      if (!res.ok) { const err = await res.json().catch(() => ({ error: res.statusText })); throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`); }
      const reader = res.body!.getReader(); const decoder = new TextDecoder(); let buffer = ''; let failed: string | null = null; let finished = false;
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n'); buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
          try { const ev = JSON.parse(line.slice(6)) as ReviseEvent; if (ev.event === 'error') failed = String(ev.error || 'failed'); else { if (ev.event === 'done') finished = true; onEvent(ev); } } catch { /* ignore */ }
        }
      }
      if (failed) throw new Error(failed);
      // A stream that ends without "done" (network drop, proxy timeout) is not a success: the caller must not act on it.
      if (!finished) throw new Error('The connection dropped before Lykke finished — the pages that landed are kept.');
    },
    /** Beautify: 'design' a structured page as a free canvas, 'structure' a canvas into a page, or 'restyle' the deck (preset id or a brief). */
    beautify: (id: string, body: { action: 'design' | 'structure'; slide: Record<string, unknown>; brief?: string } | { action: 'restyle'; brief: string }): Promise<{ slide?: Record<string, unknown>; composition?: string; settings?: DeckSettings; note?: string }> =>
      post(`/v1/presentations/${encodeURIComponent(id)}/beautify`, body),
    /** Guardrails + fact check for one working-copy page (right after Kai wrote it). */
    /** A need → a proposal (deck / page in an existing deck / template / answer). Creates nothing. */
    triage: (body: { need: string; brandId?: string; templates: Array<{ id: string; name: string; description: string; needs: string }>; campaigns: Array<{ id: string; name: string; status?: string }>; language?: string; redirect?: string }): Promise<{ proposal: NeedProposal }> =>
      post('/v1/presentations/triage', body),
    /** The ghost deck: a brief → one row per page (layout, title, description, elements) to edit and approve before anything is written. */
    plan: (id: string, body: { brief: string; pages?: number; catalogue: Array<{ id: string; label: string; category?: string; hint?: string; type?: string }>; existing?: Array<{ title?: string; type?: string }>; language?: string; single?: boolean }): Promise<DeckPlan> =>
      post(`/v1/presentations/${encodeURIComponent(id)}/plan`, body),
    /** Same, streamed: `onPage` fires as each row is decided, so ghost pages appear one by one. */
    planStream: async (id: string, body: { brief: string; pages?: number; catalogue: Array<{ id: string; label: string; category?: string; hint?: string; type?: string }>; existing?: Array<{ title?: string; type?: string }>; language?: string; single?: boolean }, onPage: (page: PlanPage, index: number) => void, onNote?: (note: string) => void): Promise<DeckPlan> => {
      const res = await fetch(`${API_BASE_URL}/v1/presentations/${encodeURIComponent(id)}/plan?stream=1`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json().catch(() => ({ error: res.statusText })); throw new Error((err as { error?: string }).error || `Request failed: ${res.status}`); }
      const reader = res.body!.getReader(); const decoder = new TextDecoder(); let buffer = ''; let result: DeckPlan | null = null; let error: string | null = null; let event = '';
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n'); buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith('event: ')) { event = line.slice(7).trim(); continue; }
          if (!line.startsWith('data: ')) continue;
          try { const d = JSON.parse(line.slice(6)); if (event === 'page') onPage(d.page, d.index); else if (event === 'note') onNote?.(String(d.note || '')); else if (event === 'done') result = d; else if (event === 'error') error = String(d.error || 'failed'); } catch { /* partial line */ }
        }
      }
      if (error) throw new Error(error);
      if (!result) throw new Error('The connection dropped while planning');
      return result;
    },
    checkPage: (id: string, body: { slide: Record<string, unknown>; page: number }): Promise<{ page: number; findings: GuardrailFinding[]; facts?: { available: boolean; reason?: string; source?: 'campaign' | 'brand'; figures: number; charts?: number; unjudged?: number; verified: number; unverified: number }; ms: number }> =>
      post(`/v1/presentations/${id}/check-page`, body),

    /** Trigger a download of the presentation as a PDF (rendered server-side via Puppeteer). */
    exportPdf: async (id: string): Promise<Blob> => {
      const res = await fetch(`${BASE_URL}/v1/presentations/${id}/export-pdf`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error((err as { error?: string }).error || `PDF export failed: ${res.status}`);
      }
      return res.blob();
    },

    /**
     * Trigger a PPTX download.
     *   mode='pixel-perfect' (default): each slide is a full-bleed PNG —
     *     visually identical to the HTML viewer, NOT editable text.
     *   mode='editable': real text shapes via pptxgenjs — editable in
     *     PowerPoint/Keynote, but won't pixel-match the modal exactly.
     */
    exportPptx: async (id: string, mode: 'pixel-perfect' | 'editable' = 'pixel-perfect'): Promise<Blob> => {
      const qs = mode === 'editable' ? '?mode=editable' : '';
      const res = await fetch(`${BASE_URL}/v1/presentations/${id}/export-pptx${qs}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error((err as { error?: string }).error || `PPTX export failed: ${res.status}`);
      }
      return res.blob();
    },

    /** Opt-in: regenerate a Gamma-polished PPTX (consumes Gamma credits). */
    exportGamma: (id: string): Promise<{ documentUrl: string; title: string; format: string }> =>
      post(`/v1/presentations/${id}/export-gamma`, {}),
  },

  okrs: {
    list: (brandId?: string): Promise<{ okrs: OKR[] }> =>
      get(`/v1/okrs${brandId ? `?brandId=${brandId}` : ''}`),
    create: (data: {
      title: string; description?: string; type: 'objective' | 'key_result';
      parentId?: string; ownerAgentId?: string; targetValue?: number;
      metricUnit?: string; dueDate?: string; brandId?: string;
    }): Promise<OKR> =>
      post('/v1/okrs', data),
    update: (id: string, data: Partial<OKR>): Promise<OKR> =>
      patch(`/v1/okrs/${id}`, data),
    delete: (id: string): Promise<{ deleted: boolean }> =>
      del(`/v1/okrs/${id}`),
    updateProgress: (id: string, currentValue: number): Promise<OKR> =>
      post(`/v1/okrs/${id}/progress`, { currentValue }),
  },

  approvals: {
    list: (brandId?: string, status?: string): Promise<{ approvals: Array<{
      id: string; team_id: string; brand_id: string; type: string; title: string;
      requested_by: string; status: string; payload: string; user_response: string | null;
      created_at: string; resolved_at: string | null;
    }> }> => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (brandId) params.set('brandId', brandId);
      const qs = params.toString();
      return get(`/v1/approvals${qs ? `?${qs}` : ''}`);
    },

    resolve: (id: string, status: 'approved' | 'rejected', response?: string): Promise<unknown> =>
      patch(`/v1/approvals/${id}`, { status, response }),
  },

  ga4: {
    /** Get the GA4 OAuth connect URL for a brand */
    connectUrl: (brandId?: string) => {
      const token = getTokenFromStorage();
      return `${BASE_URL}/v1/integrations/ga4/connect?brandId=${brandId || 'default'}${token ? `&token=${token}` : ''}`;
    },
    /** List available GA4 properties after OAuth connection */
    properties: (brandId?: string): Promise<{ properties: Array<{ propertyId: string; displayName: string; accountName: string }> }> =>
      get(`/v1/integrations/ga4/properties${brandId ? `?brandId=${brandId}` : ''}`),
    /** Save selected GA4 property for a brand */
    selectProperty: (brandId: string, propertyId: string, propertyName: string): Promise<{ success: boolean }> =>
      post('/v1/integrations/ga4/select-property', { brandId, propertyId, propertyName }),
    /** Check if GA4 is connected for a brand */
    status: (brandId?: string): Promise<{ connected: boolean; propertyName?: string }> =>
      get(`/v1/integrations?provider=ga4${brandId ? `&brandId=${brandId}` : ''}`).then((data: any) => {
        const integration = data.integrations?.find((i: any) => i.provider === 'ga4');
        if (!integration?.metadata) return { connected: false };
        const meta = JSON.parse(integration.metadata);
        return { connected: !!meta.propertyId, propertyName: meta.propertyName };
      }).catch(() => ({ connected: false })),
  },

  slack: {
    /** Get the Slack OAuth connect URL */
    connectUrl: () => {
      const token = getTokenFromStorage();
      return `${BASE_URL}/v1/integrations/slack/connect${token ? `?token=${token}` : ''}`;
    },
    /** Check if Slack is connected for the team */
    status: (): Promise<{ connected: boolean; workspaceName?: string }> =>
      get('/v1/integrations/slack/status'),
  },

  files: {
    list: (params?: { type?: string; agentId?: string; source?: string; brandId?: string }): Promise<{
      files: Array<{
        id: string;
        name: string;
        type: 'pptx' | 'docx' | 'xlsx' | 'pdf' | 'image' | 'html' | 'other';
        url: string;
        size?: number;
        agentId?: string;
        agentName?: string;
        source: 'task' | 'skill' | 'chat' | 'upload';
        taskId?: string;
        taskTitle?: string;
        createdAt: string;
      }>;
    }> => {
      const qs = new URLSearchParams();
      if (params?.type) qs.set('type', params.type);
      if (params?.agentId) qs.set('agentId', params.agentId);
      if (params?.source) qs.set('source', params.source);
      if (params?.brandId) qs.set('brandId', params.brandId);
      const q = qs.toString();
      return get(`/v1/files${q ? `?${q}` : ''}`);
    },
  },

  heartbeats: {
    /** List all heartbeats for the current team */
    list: (): Promise<Array<{
      id: string; agentId: string; cron: string; prompt: string;
      enabled: boolean; scheduled: boolean; lastRunAt: string | null;
      priority: string; goalContext: string | null;
    }>> => get('/v1/heartbeats'),
    /** Toggle a heartbeat on/off */
    toggle: (id: string, enabled: boolean): Promise<{ id: string; enabled: boolean }> =>
      patch(`/v1/heartbeats/${id}`, { enabled }),
  },

  stripe: {
    /** Get current plan & credit balance */
    getPlan: (): Promise<PlanInfo> => get('/v1/stripe/plan'),
    /** Create a Stripe checkout session and return the URL */
    createCheckout: (plan: 'startup' | 'growth'): Promise<{ url: string }> =>
      post('/v1/stripe/checkout', { plan }),
  },
};

export interface DashboardSourceIdea { text: string; templateId?: string }
export interface DashboardSource {
  id: string; name: string; description: string;
  status: 'live' | 'available' | 'setup' | 'planned';
  statusNote?: string; rows?: number | null; fields: string[]; ideas: DashboardSourceIdea[];
}
export type Visibility = 'team' | 'members' | 'private';
export type SharingVisibility = 'private' | 'shared' | 'members' | 'team' | 'public';
/** Who can see a Cowork item (owner/visibility/shared_with on every table). */
export interface SharingInfo { ownerId: string | null; visibility: SharingVisibility; sharedWith: string[]; isOwner: boolean; canEdit: boolean; /** No owner yet (made before ownership existed): the first sharing choice, or claim, sets one. */ unowned?: boolean }
export interface VisibilityInfo { visibility: Visibility; allowedUserIds: string[]; createdBy: string | null; isOwner: boolean; /** No owner yet — see SharingInfo.unowned. */ unowned?: boolean }
export interface TeamPerson { userId: string; name: string; email?: string; role?: string }
export interface MediaPlanBeat { id: string; name: string; platform: string; beatType: 'IGCP' | 'IGC' | 'BOOST' | 'AMPLI' | 'PAIDAD' | 'ORGPOST'; creators: number; band: string; pricePerCreator: number; budget: number; productTypes: Record<string, number>; dateDrafts: string; dateDeliver: string; notes?: string; complexityPct?: number; objective?: string; media?: string; cpm?: number; ugc?: { difficulty: 'easy' | 'medium' | 'hard'; months: 3 | 6 | 12 };
  /** 2026/27 pricing: Beatly's creator-side fee on this line (0–100); unset = the plan's default. Lower it for a creator discount. */
  creatorTakePct?: number;
  licensing?: { paidMonths?: 1 | 3 | 6; owned?: '12m' | 'unlimited' };
  /** UGC under the 2026/27 pricing: content per creator; the brand price is fixed by it. */
  ugcContent?: { videos?: number; imageSets?: number } }
/** 2026/27 pricing (backend engine/pricing.ts). The client never sees payouts, the creator-side fee or CPM. */
/** Where a deck's budget table came from, so the editor can refresh it from the plans. */
/** A deck's automatic verification (every figure checked against the data; what was not verified fixed automatically). */
export interface DeckVerification { available?: boolean; at?: string; threshold?: number; figures?: number; verified?: number; remaining?: Array<{ page: number; label: string; excerpt: string }>; stale?: boolean }

export type DeckChatEvent =
  | ({ event: 'verified' } & DeckVerification)
  | { event: 'status'; label: string }
  | { event: 'text'; delta: string }
  | { event: 'deck'; slides: Array<Record<string, unknown>>; title?: string }
  | { event: 'page'; index: number; state: 'writing' | 'done' | 'failed'; id?: string }
  | { event: 'go_to'; index: number }
  | { event: 'present' }
  | { event: 'decisions'; items: Array<{ question: string; options: string[] }> }
  | { event: 'done'; reply: string; changed: boolean }
  | { event: 'error'; error: string };

export type TellOp =
  | { op: 'go_to'; page: number }
  | { op: 'move'; page: number; to: number }
  | { op: 'delete'; pages: number[] }
  | { op: 'duplicate'; page: number }
  | { op: 'add'; after: number; brief: string }
  | { op: 'revise'; instruction: string };

export type ReviseEvent =
  | { event: 'plan'; scope: 'deck' | 'pages' | 'page'; pages: number[]; reply: string; brand: { id: string; name: string } | null; needsData: boolean }
  | { event: 'progress'; label: string }
  | { event: 'working'; index: number }
  | { event: 'page'; index: number; slide: Record<string, unknown> }
  | { event: 'page-error'; index: number; error: string }
  | { event: 'done'; changed: number; of?: number; brand?: { id: string; name: string } | null }
  | { event: 'error'; error: string };

export interface PlansTableSource { kind: 'media-plans'; ids: string[]; rounding: 'exact' | '50k' | '100k'; lang: 'sv' | 'en'; /** Chosen months, or null = follow the plans' dates and the client's setting. */ months?: number | null; at: string }
export interface CommercialTerms { version: 2; catalogue: string; engagement: 'retainer' | 'adhoc'; agencyFeePct?: number; feeMode: 'included' | 'on_top'; creatorTakePct: number; addons?: Array<{ type: 'seeding' | 'production'; package?: string; pieces?: number }> }
export interface ClientPricingTerms { engagement: 'retainer' | 'adhoc'; feeMode: 'included' | 'on_top'; agencyFeePct?: number; creatorTakePct: number; rateCard: Record<string, number>; subscription: { monthly: number; months: number; tracking: boolean } }
export interface PricingCatalogue { id: string; asOf: string; source: string; agencyFeePct: number; adhocFeePct: number; ugc: { perCreator: number; perVideo: number; perImageSet: number }; creatorTakePct: number; subscription: { monthly: number; tracking: number }; seeding: Array<{ id: string; label: string; seeds: number; price: number }>; productionPerPiece: number; minimumBudget: { amount: number; currency: string }; retainerMinMonths: number }
export interface EconomicsLine { beatId?: string; kind: 'creators' | 'ugc' | 'paid' | 'seeding' | 'production'; label: string; clientPrice: number; payout: number; beatlyRevenue: number; creatorTakePct?: number; licensingUpliftPct?: number; creators?: number; impressions: number }
export interface PlanEconomics { version: 2; catalogue: string; currency: string; engagement: 'retainer' | 'adhoc'; feeMode: 'included' | 'on_top'; lines: EconomicsLine[]; linesTotal: number; agencyFee: { pct: number; amount: number }; adhocFee: { pct: number; amount: number }; clientTotal: number; unallocated: number; creators: { count: number; gross: number; payout: number; beatlyCut: number }; paidMedia: number; revenue: { agencyFee: number; adhocFee: number; creatorSide: number; addons: number; total: number }; takeRatePct: number | null; impressions: number; warnings: string[] }
export interface ProgrammeEconomics { currency: string; plans: Array<{ id: string; name: string; economics: PlanEconomics }>; subscription: { monthly: number; months: number; tracking: number; total: number }; clientTotal: number; linesTotal: number; agencyFee: number; unallocated: number; creators: PlanEconomics['creators']; paidMedia: number; revenue: { agencyFee: number; adhocFee: number; creatorSide: number; addons: number; subscription: number; total: number }; takeRatePct: number | null; impressions: number; warnings: string[] }
export interface PriceBook { currency: string; asOf: string; bands: Array<{ id: string; label: string; followers: string; cost: number; avgReach: number }>; organicCpm: number; boosting: Array<{ id: string; label: string; cpm: { small: number; medium: number; large: number }; rate: number; rateLabel: string; benchmark?: string }>; metaFeePct: Record<string, number>; amplificationTakeRatePct: number; amplification: Array<{ id: string; group: string; label: string; cpm: number; platform: string }>; ugcUnitPrice: Array<{ maxPerMonth: number | null; months: Record<string, number> }>; ugcPackages: Array<{ id: string; label: string; perMonth: number; months: Record<string, number> }>; ugcDifficulty: Array<{ id: string; label: string; spend: number; client: number; hint: string }>; defaults: { spendShare: number; boostShareOfSpend: number; ugcShareOfSpend: number; targetMarginPct: number } }
export interface MediaPlanTotals { fee: number; media: number; allocated: number; unallocated: number; creatorBudget: number; paidBudget: number; creators: number; impressions: number; cpmSek: number | null; costs: number; netRevenue: number; takeRatePct: number | null; marginPct: number | null; clicks: number; ugcClientPrice: number; size: 'small' | 'medium' | 'large' }
export interface MarketBrief { market: string; countryCode: string; generatedAt: string; model: string; snapshot: string; swot: { strengths: string[]; weaknesses: string[]; opportunities: string[]; threats: string[] }; competitors: Array<{ name: string; angle: string }>; trends: string[]; basis: { competitors: number; promotables: number; wikiPages: number } }
export interface MediaPlanPreset { id: string; name: string; tagline: string; objective: 'awareness' | 'consideration' | 'traffic' | 'conversion' | 'content' | 'engagement'; useWhen: string[]; notFor: string; platforms: string[]; products: Array<'creator' | 'ugc' | 'boost' | 'ampli'>; boostObjective: string | null; boostShare: number; flightWeeks: [number, number]; primaryKpi: string; questions: string[]; typical: { budgetSek: [number, number, number]; days: number; note: string } }
export interface BundleItem { kind: 'proposal' | 'plan'; id: string; title: string }
export interface Bundle { id: string; teamId: string; brandId: string | null; name: string; items: BundleItem[]; createdBy: string | null; createdAt: string; updatedAt: string }
export interface BundleSources { proposals: Array<{ presentationId: string; title: string; brandId: string; brandName: string; campaignName?: string; objective?: string; budget?: number; currency?: string; market?: string; startDate?: string; endDate?: string; complete: boolean; updatedAt: string }>; plans: Array<{ id: string; name: string; clientName: string | null; market: string | null; currency: string; totalBudget: number; updatedAt: string; brandId: string | null }> }
export interface MediaPlan { id: string; teamId: string; brandId: string | null; name: string; clientName: string | null; market: string | null; currency: string; totalBudget: number; takeRatePct: number | null; beats: MediaPlanBeat[]; source: Record<string, unknown> | null; status: string; kickoffId: string | null; presentationId: string | null; createdBy: string | null; createdAt: string; updatedAt: string; totals: MediaPlanTotals; economics?: PlanEconomics | null; agreement?: { id: string; url: string; at: string } | null; sharing?: SharingInfo }
export interface PastCampaign { id: string; name: string; budget: number; currency: string; status: string; start?: string; end?: string; beats: Array<{ id: string; type: string; name: string; budget: number; start?: string; end?: string }> }
export interface MediaPlanBenchmarks { source: 'team' | 'global'; campaigns: number; takeRatePct: number | null; budgetMedian: number | null; currency: string; pastCampaigns: PastCampaign[]; global: { asOf: string; campaigns: number; takeRatePct: { p25: number; median: number; p75: number }; campaignBudgetSek: { p25: number; median: number; p75: number }; mix: { creatorOnly: number; creatorPlusBoost: number }; boostShareOfCreatorBeat: number; beatsPerCampaign: { median: number; p75: number } } }
export interface DealBenchmarks { year: string; deals: number; budgetSek: { median: number | null; p25: number | null; p75: number | null }; agencyFeePct: number | null; payoutFeePct: number | null; budgetPerProfileSek: number | null; products: Record<string, number>; platforms: Record<string, number>; markets: Record<string, number>; list: Array<{ id: string; name: string | null; client: string | null; signedAt: string | null; total: number | null; currency: string | null; markets: string[]; profiles?: { min?: number; max?: number }; platforms: string[]; agencyFeePct?: number; payoutFeePct?: number; products: string[] }> }
export interface MediaPlanChatResult { say: string; applied: Array<{ id: string; label: string; value: string | number }>; skipped: Array<{ id: string; reason: string }>; missing: string[]; plan: MediaPlan | null; totals: MediaPlanTotals | null; steps: Array<{ id: string; label: string; done: boolean }> }
/** What the plan does on the people it aims at (server `engine/reachMath`): per-line reach, the union, frequency, TRP. */
export interface PlanReach { universe: { people: number; basis: 'addressable' | 'on_platform' | 'population' | 'none'; source: string }; lines: Array<{ key: string; name: string; beatType: string; platform: string; impressions: number; uniqueReach: number; frequency: number; universe: number }>; impressions: number; uniqueReach: number; reachPctUniverse: number | null; reachPctPopulation: number | null; avgFrequency: number | null; trp: number | null; cpp: number | null; costPerThousandReached: number | null; assumptions: string[] }
export interface PlanReachTarget { pct: number; people: number; universe: number; frequency: number | null; budget: number; currency: string; at: string }
export interface PlanAudienceSize { reach?: PlanReach; reachTarget?: PlanReachTarget | null; market: string; fullAudience: boolean; at: string; audiences: Array<{ id: string; name: string; ageRange: string | null; genders: string[]; market: string; population: number | null; populationSource: string; onPlatform: Partial<Record<'INSTAGRAM' | 'TIKTOK' | 'YOUTUBE' | 'FACEBOOK' | 'SNAPCHAT', number>>; addressable: { lower: number; upper: number; mid: number; at: string; source: string } | null; missing: string[] }>; union: { ageRange: string | null; assumedAges?: boolean; genders: string[]; population: number | null; onPlatform: Partial<Record<'INSTAGRAM' | 'TIKTOK' | 'YOUTUBE' | 'FACEBOOK' | 'SNAPCHAT', number>>; addressableMid: number | null; note: string } | null; gate: { ok: boolean; message: string; missing: Array<{ id: string; name: string; missing: string[] }> } }
export interface ProposalChannelFromPlan { id: string; share: number; budget: number; creators: number; band: string; deliverables: string; impressions: number; cpm: number; rationale: string; key?: string; name?: string; platform?: string; beatType?: string; objective?: string; performanceGoal?: string }

export interface PublicPresentation { id: string; title: string; brandId: string; slides: Array<Record<string, unknown>>; settings?: DeckSettings | null; brand: Record<string, unknown>; updatedAt: string }
export interface PublicDashboard {
  id: string; title: string; description: string | null; brandId: string;
  lastRefreshedAt: string | null; updatedAt: string; hasHtml: boolean; renderStatus: string;
  brand: Record<string, unknown> | null; spec: any; data: Record<string, any> | null; dataMeta?: unknown; kaiNote?: string | null;
}
export interface DashboardPreflight {
  templateId: string;
  level: 'ready' | 'partial' | 'empty';
  sources: Array<{ id: string; label: string; kind: string; count: number; error?: string; message?: string }>;
  widgets: Array<{ id: string; title: string; type: string; hasData: boolean; reason?: string }>;
  missing: string[];
  checkedAt: string;
}
export interface DashboardSourceGroup { id: string; name: string; blurb: string; sources: DashboardSource[] }

export interface KickoffProposal {
  presentationId: string; title: string; brandId: string; brandName: string; clientName: string;
  campaignName?: string; objective?: string; budget?: number; currency?: string; market?: string;
  startDate?: string; endDate?: string; product?: string; step?: number; complete: boolean; updatedAt: string;
}
